/**
 * The system-defined notification event catalogue (CLAUDE.md invariant 8: the contract lives here).
 *
 * Settled product decision: modules cannot invent notification keys. Every event a module wants to
 * raise must be declared here first, in its own PR, exactly like a ts-rest contract route — this
 * file IS the contract for what `emitNotification()` (a future PR) will accept. A module that could
 * mint its own key could also mint its own "mandatory" or "external" flag, which is exactly the kind
 * of self-graded importance `packages/notify/src/preferences.ts`'s mandatory list is designed to
 * resist (see that file's own header comment).
 *
 * ONE ENTRY PER KEY IN `packages/notify/src/templates.ts` `BUILTIN_FAMILIES`, no more, no fewer —
 * enforced by `packages/notify/src/catalog-parity.test.ts`, which this package cannot import (the
 * dependency only runs `notify -> shared`, never back). `mandatory`/`external` are likewise kept in
 * lockstep with `DEFAULT_MANDATORY_KEY_PATTERNS` (`preferences.ts`) and the four keys passed to
 * `notifyExternal()` (`external.ts`) by that same test, so this file and `packages/notify` cannot
 * drift against each other silently.
 */

// ---------------------------------------------------------------------------
// Modules
// ---------------------------------------------------------------------------

/**
 * One entry per distinct first segment across every `BUILTIN_FAMILIES` key today, plus `chat` for
 * the `chat.message` family this PR adds. This is deliberately the literal key-prefix list, not a
 * curated "product module" list — `approval`, `on_duty`, and `profile_update` are each their own
 * entry here even though they might otherwise be grouped under a broader module, because the module
 * × channel admin screen (a later PR) has to offer a toggle for every prefix a real key actually
 * uses, not for a taxonomy invented after the fact.
 */
export const NOTIFICATION_MODULES = [
  'leave',
  'expense',
  'approval',
  'workflow',
  'attendance',
  'timesheet',
  'work_plan',
  'project',
  'shift',
  'on_duty',
  'account',
  'org',
  'profile_update',
  'resignation',
  'fnf_settlement',
  'transfer',
  'offboarding',
  'requisition',
  'interview',
  'offer',
  'candidate',
  'onboarding',
  'document_task',
  'payroll',
  'review',
  'feedback',
  'one_on_one',
  'recognition',
  'performance',
  'goal',
  'improvement_plan',
  'calibration',
  'ai',
  'chat',
  'billing',
  'asset',
  'policy',
  'training',
  'announcement',
  'engagement',
  'helpdesk',
  'survey',
  'agreement',
  'travel',
  'social',
  // Connected applications (ADR 0033): sent to holders of app_integration:manage.
  'app_integration',
] as const;

export type NotificationModule = (typeof NOTIFICATION_MODULES)[number];

// ---------------------------------------------------------------------------
// Events
// ---------------------------------------------------------------------------

export interface NotificationEvent {
  /** Matches a `BUILTIN_FAMILIES` key in `packages/notify` exactly — the dotted event name. */
  readonly key: string;
  /** The key's first dotted segment. Must be a member of `NOTIFICATION_MODULES`. */
  readonly module: string;
  /** Short label for the admin Rules & Channels grid. */
  readonly displayName: string;
  /** One sentence explaining when this fires, for the same screen. */
  readonly description: string;
  /**
   * Statutory, policy, or security notice a person cannot mute even by turning every channel off
   * for its module. Must match `DEFAULT_MANDATORY_KEY_PATTERNS` (`packages/notify/src/preferences.ts`)
   * exactly — see the parity test.
   */
  readonly mandatory?: boolean;
  /**
   * Sent via `notifyExternal()` to someone with no `Person` record (a candidate) rather than via
   * `notify()`. Must be exactly the four keys `notifyExternal()` is called with — see the parity
   * test. External events bypass the module × channel toggles entirely (design rule 9 in the plan):
   * an org muting "recruitment -> email" for internal noise must not silently stop an offer letter
   * reaching a candidate.
   */
  readonly external?: boolean;
  /**
   * False hides the event from the admin config screen even though the catalogue lists it — for an
   * event whose only channel needs infrastructure that has not shipped yet (the web push vertical
   * slice, PR 7 of the plan). Undefined/true for every event today; nothing currently needs it off.
   */
  readonly available?: boolean;
  /**
   * A relative path template for the employee bell's deep link, using `{{variable}}` placeholders.
   * Left undefined when the event has no single obvious destination record (a welcome email, a
   * generic multi-type approval reminder) — see `resolveHref()` below for why an unresolvable
   * template becomes no link rather than a wrong one.
   */
  readonly href?: string;
  /**
   * The payload keys the matching template family actually interpolates. Copied verbatim from that
   * family's own `variables` array in `templates.ts` — a future payload-validation step (PR 3 of the
   * plan) throws at emit time, in the caller's transaction, when a required one is missing, per
   * design rule 4: better a compile-time-adjacent throw in the route handler than a burned BullMQ
   * retry budget in the worker.
   */
  readonly requiredVariables?: readonly string[];
  /**
   * Channels on which this notification is OFF until a company switches it on for this notification
   * (a per-notification row in the Rules screen). Used for opt-in notifications such as the nightly
   * manager exceptions email. A module-level switch does not turn it on; only a per-notification one.
   */
  readonly defaultDisabledChannels?: readonly string[];
}

export const NOTIFICATION_EVENT_CATALOG: readonly NotificationEvent[] = [
  // ---- Leave --------------------------------------------------------------------
  {
    key: 'leave.requested',
    module: 'leave',
    displayName: 'Leave requested',
    description: 'An employee submits a leave request that needs their approver to act on it.',
    href: '/leave/requests/{{leaveRequestId}}',
    requiredVariables: [
      'requesterName',
      'leaveType',
      'startDate',
      'endDate',
      'dayCount',
      'reason',
      'appName',
    ],
  },
  {
    key: 'leave.approved',
    module: 'leave',
    displayName: 'Leave approved',
    description: "An employee's leave request is approved.",
    href: '/leave/requests/{{leaveRequestId}}',
    requiredVariables: ['approverName', 'leaveType', 'startDate', 'endDate', 'balanceAfter'],
  },
  {
    key: 'leave.rejected',
    module: 'leave',
    displayName: 'Leave declined',
    description: "An employee's leave request is declined.",
    href: '/leave/requests/{{leaveRequestId}}',
    requiredVariables: ['approverName', 'leaveType', 'startDate', 'endDate', 'reason'],
  },
  {
    key: 'leave.cancelled',
    module: 'leave',
    displayName: 'Leave request cancelled',
    description: 'An employee cancels a leave request that was waiting on their approver.',
    href: '/approvals',
    requiredVariables: ['requesterName', 'leaveType', 'startDate', 'endDate', 'appName'],
  },
  {
    key: 'leave.encashment_requested',
    module: 'leave',
    displayName: 'Leave encashment requested',
    description: 'An employee asks to be paid for leave days and the request needs their approver.',
    href: '/approvals',
    requiredVariables: ['requesterName', 'leaveType', 'days', 'appName'],
  },
  {
    key: 'leave.encashment_decided',
    module: 'leave',
    displayName: 'Leave encashment decided',
    description: "An employee's leave encashment request is approved or declined.",
    href: '/leave',
    requiredVariables: ['approverName', 'leaveType', 'days', 'decision'],
  },
  {
    key: 'leave.comp_off_decided',
    module: 'leave',
    displayName: 'Comp-off request decided',
    description: "An employee's request to be credited comp-off is approved or declined.",
    href: '/leave',
    requiredVariables: ['approverName', 'workedDate', 'days', 'decision'],
  },
  {
    key: 'leave.cancellation_decided',
    module: 'leave',
    displayName: 'Leave cancellation decided',
    description: 'A request to cancel all or part of approved leave is approved or declined.',
    href: '/leave',
    requiredVariables: ['approverName', 'leaveType', 'startDate', 'endDate', 'decision'],
  },
  {
    key: 'leave.amendment_decided',
    module: 'leave',
    displayName: 'Leave change decided',
    description: 'A request to move approved leave to other dates is approved or declined.',
    href: '/leave',
    requiredVariables: ['approverName', 'leaveType', 'startDate', 'endDate', 'decision'],
  },
  {
    key: 'leave.cancelled_on_behalf',
    module: 'leave',
    displayName: 'Leave cancelled for you',
    description: "A manager or HR cancels all or part of an employee's leave for them.",
    href: '/leave',
    requiredVariables: ['actorName', 'leaveType', 'startDate', 'endDate', 'appName'],
  },

  {
    key: 'leave.task_delegated',
    module: 'leave',
    displayName: 'Named as a leave cover',
    description: "An employee names this person to cover their work while they're on leave.",
    requiredVariables: ['requesterName', 'leaveType', 'startDate', 'endDate'],
  },
  {
    key: 'leave.balance_credited',
    module: 'leave',
    displayName: 'Leave balance credited',
    description: "An accrual or carry-forward adds days to this person's leave balance.",
    requiredVariables: ['leaveType', 'amount', 'leaveYear', 'balanceAfter', 'appName'],
  },

  // ---- Expense --------------------------------------------------------------------
  {
    key: 'expense.approved',
    module: 'expense',
    displayName: 'Expense claim approved',
    description: "An employee's expense claim is approved.",
    href: '/claims?open={{claimId}}',
    requiredVariables: [
      'approverName',
      'categoryName',
      'reference',
      'amount',
      'currency',
      'comment',
    ],
  },
  {
    key: 'expense.rejected',
    module: 'expense',
    displayName: 'Expense claim declined',
    description: "An employee's expense claim is declined.",
    href: '/claims?open={{claimId}}',
    requiredVariables: [
      'approverName',
      'categoryName',
      'reference',
      'amount',
      'currency',
      'comment',
    ],
  },
  {
    key: 'expense.sent_back',
    module: 'expense',
    displayName: 'Expense claim sent back',
    description: 'An approver sends an expense claim back to the employee for changes.',
    href: '/claims?open={{claimId}}',
    requiredVariables: [
      'approverName',
      'categoryName',
      'reference',
      'amount',
      'currency',
      'comment',
    ],
  },
  {
    key: 'expense.comment_posted',
    module: 'expense',
    displayName: 'New comment on a claim, report or travel request',
    description:
      'The claimant or an approver leaves a comment on a claim, report or travel request.',
    // No single destination — `entityType` varies per call site (a claim, a report, a travel
    // request each have their own screen), same reasoning `approval.pending`'s own comment gives.
    requiredVariables: ['authorName', 'entityLabel', 'reference', 'bodyPreview'],
  },
  {
    key: 'expense.settled',
    module: 'expense',
    displayName: 'Expense claim paid',
    description: 'Finance records an approved expense claim as paid out.',
    href: '/claims?open={{claimId}}',
    requiredVariables: [
      'financeName',
      'categoryName',
      'reference',
      'amount',
      'currency',
      'payoutMode',
      'payoutReference',
    ],
  },
  {
    // EXP-P2-14 (Keka parity) — a bank bounce/return on the claim's payout leg, reported by
    // Finance after the fact. The claim is back to approved, free to be paid again.
    key: 'expense.payment_failed',
    module: 'expense',
    displayName: 'Expense payment failed',
    description: "A settled claim's payment bounced and it has reverted to approved.",
    href: '/claims?open={{claimId}}',
    requiredVariables: ['reference', 'reason'],
  },
  {
    // ADDED 2026-09-22 — audit-confirmed gap: Finance had to manually poll the Settlement queue,
    // nothing pushed when a claim became actionable. Sent to every `finance` role holder org-wide,
    // not one person, unlike every other expense.* notification above.
    key: 'expense.ready_to_settle',
    module: 'expense',
    displayName: 'Expense claim ready to settle',
    description: 'An expense claim finished approval and is now waiting on Finance to pay it out.',
    href: '/claims?open={{claimId}}',
    requiredVariables: ['employeeName', 'categoryName', 'reference', 'amount', 'currency'],
  },
  {
    // R-P5 — apps/worker/src/workers/expense-reminder.ts's daily sweep. No single claim to deep
    // link to (could be several drafts), unlike every other expense.* entry here.
    key: 'expense.draft_reminder',
    module: 'expense',
    displayName: 'Unsubmitted expense claim reminder',
    description: 'A daily nudge that you have a draft expense claim that has not been submitted.',
    href: '/claims',
    requiredVariables: ['count', 'appName'],
  },
  {
    key: 'expense.pending_reminder',
    module: 'expense',
    displayName: 'Pending expense claim reminder',
    description: 'A daily nudge that an expense claim is still waiting on your decision.',
    href: '/claims?open={{claimId}}',
    requiredVariables: ['reference', 'amount', 'currency', 'appName'],
  },
  {
    // EXP-P2-10 — "Reminders for pending reports and advances — Claims only" (the review's own
    // line). Same shape as expense.draft_reminder, for the Advances tab.
    key: 'expense.advance.draft_reminder',
    module: 'expense',
    displayName: 'Unsubmitted advance request reminder',
    description: 'A daily nudge that you have a draft advance request that has not been submitted.',
    href: '/claims?tab=advances',
    requiredVariables: ['count', 'appName'],
  },
  {
    key: 'expense.advance.pending_reminder',
    module: 'expense',
    displayName: 'Pending advance request reminder',
    description: 'A daily nudge that an advance request is still waiting on your decision.',
    href: '/claims?tab=advances&openAdvance={{advanceId}}',
    requiredVariables: ['reference', 'amount', 'currency', 'appName'],
  },
  {
    // Same shape, for multi-line expense Reports.
    key: 'expense.report.draft_reminder',
    module: 'expense',
    displayName: 'Unsubmitted expense report reminder',
    description: 'A daily nudge that you have a draft expense report that has not been submitted.',
    href: '/claims?tab=reports',
    requiredVariables: ['count', 'appName'],
  },
  {
    key: 'expense.report.pending_reminder',
    module: 'expense',
    displayName: 'Pending expense report reminder',
    description: 'A daily nudge that an expense report is still waiting on your decision.',
    href: '/claims?tab=reports&open={{reportId}}',
    requiredVariables: ['reference', 'amount', 'currency', 'appName'],
  },
  {
    key: 'expense.advance.approved',
    module: 'expense',
    displayName: 'Advance request approved',
    description: "An employee's trip advance request is approved.",
    href: '/claims?tab=advances&openAdvance={{advanceId}}',
    requiredVariables: ['approverName', 'purpose', 'reference', 'amount', 'currency', 'comment'],
  },
  {
    key: 'expense.advance.rejected',
    module: 'expense',
    displayName: 'Advance request declined',
    description: "An employee's trip advance request is declined.",
    href: '/claims?tab=advances&openAdvance={{advanceId}}',
    requiredVariables: ['approverName', 'purpose', 'reference', 'amount', 'currency', 'comment'],
  },
  {
    key: 'expense.report.approved',
    module: 'expense',
    displayName: 'Expense report approved',
    description: "An employee's multi-line expense report is approved.",
    href: '/claims?tab=reports&openReport={{reportId}}',
    requiredVariables: ['approverName', 'title', 'reference', 'amount', 'currency', 'comment'],
  },
  {
    key: 'expense.report.rejected',
    module: 'expense',
    displayName: 'Expense report declined',
    description: "An employee's multi-line expense report is declined.",
    href: '/claims?tab=reports&openReport={{reportId}}',
    requiredVariables: ['approverName', 'title', 'reference', 'amount', 'currency', 'comment'],
  },
  {
    key: 'travel_request.approved',
    module: 'expense',
    displayName: 'Travel request approved',
    description: "An employee's travel request is approved.",
    href: '/claims?tab=travel&openRequest={{travelRequestId}}',
    requiredVariables: ['approverName', 'destination', 'reference', 'comment'],
  },
  {
    key: 'travel_request.rejected',
    module: 'expense',
    displayName: 'Travel request declined',
    description: "An employee's travel request is declined.",
    href: '/claims?tab=travel&openRequest={{travelRequestId}}',
    requiredVariables: ['approverName', 'destination', 'reference', 'comment'],
  },
  {
    key: 'expense.advance.disbursed',
    module: 'expense',
    displayName: 'Advance disbursed',
    description: 'Finance pays out an approved advance and opens its outstanding balance.',
    href: '/claims?tab=advances&openAdvance={{advanceId}}',
    requiredVariables: [
      'financeName',
      'reference',
      'amount',
      'currency',
      'payoutMode',
      'payoutReference',
    ],
  },
  // Overdue advances and payroll recovery (travel desk TD-0.6, D22). The advance's own amount is
  // already shown by the advance templates above; nothing here carries a salary figure.
  {
    key: 'expense.advance.overdue',
    module: 'expense',
    displayName: 'Expense advance overdue',
    description:
      'A paid-out advance is still unsettled past its settle-by date. Sent daily to the employee and to the recipients chosen for this notification.',
    href: '/claims/advances/{{advanceId}}',
    requiredVariables: [
      'employeeName',
      'reference',
      'outstandingAmount',
      'currency',
      'expectedSettlementDate',
      'daysOverdue',
      'appName',
    ],
  },
  {
    key: 'expense.advance.recovery_scheduled',
    module: 'expense',
    displayName: 'Advance recovery through payroll scheduled',
    description:
      "Finance schedules the unsettled part of an employee's advance to be recovered from an upcoming payroll run.",
    href: '/claims/advances/{{advanceId}}',
    requiredVariables: ['reference', 'amount', 'currency', 'periodLabel', 'appName'],
  },
  // Travel Desk Phase 4 (TD-4.6, TD-4.8): a wrong payroll deduction refunded (EC-58), and the org
  // owner's decision on a proposed write-off back to the finance person who proposed it.
  {
    key: 'expense.advance.recovery_reversed',
    module: 'expense',
    displayName: 'Advance payroll deduction refunded',
    description:
      'A payroll deduction taken against an advance is reversed and refunded in an upcoming payroll run.',
    href: '/claims/advances/{{advanceId}}',
    requiredVariables: ['reference', 'amount', 'currency', 'periodLabel', 'appName'],
  },
  {
    key: 'expense.advance.write_off_decided',
    module: 'expense',
    displayName: 'Advance write-off decided',
    description:
      'The proposal to write off the unsettled part of an advance is approved or rejected; sent to whoever proposed it.',
    href: '/claims/advances/{{advanceId}}',
    requiredVariables: ['approverName', 'reference', 'decisionLabel', 'comment', 'appName'],
  },

  // ---- Approval (generic) -----------------------------------------------------
  {
    key: 'approval.pending',
    module: 'approval',
    displayName: 'Approval waiting on you',
    description:
      'A generic reminder that some item — of a type named in the payload — awaits this approver.',
    // No single destination: `itemType` varies per call site, so there is no one route that is
    // right for every use of this generic key.
    requiredVariables: ['requesterName', 'itemType', 'dueDate', 'appName'],
  },
  {
    key: 'approval.question',
    module: 'approval',
    displayName: 'Question asked on approval',
    description:
      'An approver asks a question on a request, pausing the approval step until answered.',
    requiredVariables: ['approverName', 'entityType', 'question', 'appName'],
  },
  {
    key: 'approval.answered',
    module: 'approval',
    displayName: 'Question answered on approval',
    description: "The requester answers the approver's question, resuming the approval step.",
    requiredVariables: ['requesterName', 'entityType', 'answer', 'appName'],
  },

  {
    key: 'approval.cc',
    module: 'approval',
    displayName: 'Copied on a request',
    description:
      'A request of a type named in the payload was filed and this person is copied on it — informed, not asked to decide.',
    requiredVariables: ['requesterName', 'itemType', 'appName'],
  },

  // ---- Workflow (generic) -------------------------------------------------------
  {
    key: 'workflow.delegation_assigned',
    module: 'workflow',
    displayName: 'Approvals delegated to you',
    description:
      'Someone sets an out-of-office delegation naming this person as their approval cover.',
    requiredVariables: ['delegatorName', 'entityType', 'validFrom', 'validTo', 'appName'],
  },

  // ---- Attendance ---------------------------------------------------------------
  {
    key: 'attendance.regularisation_approved',
    module: 'attendance',
    displayName: 'Regularisation approved',
    description: "An employee's attendance regularisation request is approved.",
    // The requester's own list (P3-1): the per-request page is the approver's review screen, which the requester
    // cannot open.
    href: '/attendance/requests',
    requiredVariables: ['approverName', 'date', 'remarks'],
  },
  {
    key: 'attendance.regularisation_rejected',
    module: 'attendance',
    displayName: 'Regularisation declined',
    description: "An employee's attendance regularisation request is declined.",
    // The requester's own list (P3-1): the per-request page is the approver's review screen, which the requester
    // cannot open.
    href: '/attendance/requests',
    requiredVariables: ['approverName', 'date', 'remarks'],
  },

  // ---- Shift ----------------------------------------------------------------------
  {
    key: 'shift.change_approved',
    module: 'shift',
    displayName: 'Shift change approved',
    description: "An employee's shift change request is approved.",
    // P3-1: the employee's own shift-change list; there is no per-request page.
    href: '/attendance/shift-change-requests',
    requiredVariables: ['approverName', 'date', 'requestedShiftLabel', 'remarks'],
  },
  {
    key: 'shift.change_rejected',
    module: 'shift',
    displayName: 'Shift change declined',
    description: "An employee's shift change request is declined.",
    // P3-1: the employee's own shift-change list; there is no per-request page.
    href: '/attendance/shift-change-requests',
    requiredVariables: ['approverName', 'date', 'requestedShiftLabel', 'remarks'],
  },

  // ---- On-duty / remote work --------------------------------------------------
  {
    key: 'on_duty.approved',
    module: 'on_duty',
    displayName: 'On-duty/remote request approved',
    description: "An employee's on-duty or remote-work request is approved.",
    // P3-1: the employee's own on-duty list; there is no per-request page.
    href: '/attendance/on-duty-requests',
    requiredVariables: ['approverName', 'startDate', 'endDate', 'mode', 'remarks'],
  },
  {
    key: 'on_duty.rejected',
    module: 'on_duty',
    displayName: 'On-duty/remote request declined',
    description: "An employee's on-duty or remote-work request is declined.",
    // P3-1: the employee's own on-duty list; there is no per-request page.
    href: '/attendance/on-duty-requests',
    requiredVariables: ['approverName', 'startDate', 'endDate', 'mode', 'remarks'],
  },

  // ---- Payroll --------------------------------------------------------------------
  {
    key: 'payroll.tax.proof_reviewed',
    module: 'payroll',
    displayName: 'Tax proof reviewed',
    description: "An employee's income-tax declaration proof is reviewed.",
    requiredVariables: ['categoryLabel', 'decision', 'remarks', 'appName'],
  },
  {
    key: 'payroll.run.submitted',
    module: 'payroll',
    displayName: 'Payroll run submitted',
    description: 'A payroll run is submitted and waiting for its approver to act on it.',
    requiredVariables: [
      'submitterName',
      'periodLabel',
      'entityName',
      'headcount',
      'netTotal',
      'appName',
    ],
  },
  {
    key: 'payroll.run.decided',
    module: 'payroll',
    displayName: 'Payroll run decided',
    description: 'A submitted payroll run is approved or sent back.',
    requiredVariables: [
      'approverName',
      'decision',
      'periodLabel',
      'entityName',
      'comment',
      'appName',
    ],
  },
  {
    key: 'payroll.run.reopened',
    module: 'payroll',
    displayName: 'Payroll run reopened',
    description:
      'A previously approved payroll run is reopened and needs recalculating and approving again.',
    requiredVariables: ['actorName', 'periodLabel', 'entityName', 'reason', 'appName'],
  },
  {
    key: 'payroll.statutory.signoff_pending',
    module: 'payroll',
    displayName: 'Statutory filing awaiting sign-off',
    description: 'A generated statutory return is waiting for its authoriser to sign off.',
    requiredVariables: [
      'preparerName',
      'returnLabel',
      'periodLabel',
      'entityName',
      'totalLiability',
      'dueOn',
      'appName',
    ],
  },
  {
    key: 'payroll.statutory.due_soon',
    module: 'payroll',
    displayName: 'Statutory deadline approaching',
    description:
      'A statutory return, TDS deposit or Form 16 issue date for an Indian legal entity falls due within the week.',
    requiredVariables: ['itemLabel', 'entityName', 'dueOn', 'appName'],
  },
  {
    key: 'payroll.form16.issued',
    module: 'payroll',
    displayName: 'Form 16 issued',
    description: "An employee's Form 16 for a fiscal year is issued and ready to download.",
    requiredVariables: ['fiscalYearLabel', 'entityName', 'appName'],
  },
  {
    key: 'payroll.query.assigned',
    module: 'payroll',
    displayName: 'Salary query assigned',
    description: 'A salary query is assigned to a member of the payroll team.',
    requiredVariables: ['ticketNo', 'employeeName', 'periodLabel', 'subject', 'appName'],
  },
  {
    key: 'payroll.query.responded',
    module: 'payroll',
    displayName: 'Salary query reply',
    description: "The payroll team replies on an employee's salary query.",
    requiredVariables: ['ticketNo', 'periodLabel', 'appName'],
  },
  {
    key: 'payroll.query.resolved',
    module: 'payroll',
    displayName: 'Salary query resolved',
    description: "An employee's salary query is resolved.",
    requiredVariables: [
      'ticketNo',
      'periodLabel',
      'resolutionLabel',
      'message',
      'adjustmentNote',
      'appName',
    ],
  },
  {
    key: 'payroll.payment.batch_pending_authorisation',
    module: 'payroll',
    displayName: 'Payment batch awaiting authorisation',
    description:
      'A prepared payment batch is submitted and waiting for its authoriser to act on it.',
    href: '/admin/payroll/operations/payments',
    requiredVariables: [
      'preparerName',
      'entityName',
      'beneficiaryCount',
      'netTotal',
      'currency',
      'batchId',
      'appName',
    ],
  },
  {
    key: 'payroll.payslip_published',
    module: 'payroll',
    displayName: 'Payslip published',
    description: "An employee's payslip for a pay period is published and ready to download.",
    // Statutory: the employer must tell the employee their payslip exists (see preferences.ts).
    mandatory: true,
    href: '/admin/payroll/operations/payments',
    requiredVariables: ['periodLabel', 'entityName', 'appName'],
  },
  {
    key: 'payroll.payslip_reissued',
    module: 'payroll',
    displayName: 'Payslip reissued',
    description: "An employee's published payslip is reissued as a corrected version.",
    href: '/admin/payroll/operations/payments',
    requiredVariables: ['periodLabel', 'entityName', 'reason', 'appName'],
  },

  // ---- Account & security ------------------------------------------------------
  {
    key: 'account.welcome',
    module: 'account',
    displayName: 'Welcome',
    description: "A new employee's account is provisioned and ready to sign in.",
    // No single record this points at — it is a welcome message, not an item to review.
    requiredVariables: ['firstName', 'orgName', 'appUrl'],
  },
  {
    key: 'account.email_verification',
    module: 'account',
    displayName: 'Verify email',
    mandatory: true,
    description: 'A new account must verify its email address before it can sign in.',
    // `verifyUrl` is already a full, signed link — not an in-app route to deep-link the bell to.
    requiredVariables: ['firstName', 'orgName', 'verifyUrl', 'expiresInMinutes'],
  },
  {
    key: 'account.password_reset',
    module: 'account',
    displayName: 'Password reset requested',
    mandatory: true,
    description: 'Someone requested a password reset link for this account.',
    // Same reasoning as email_verification: `resetUrl` is the destination, not an app route.
    requiredVariables: ['firstName', 'orgName', 'resetUrl', 'expiresInMinutes'],
  },
  {
    key: 'account.invite',
    module: 'account',
    displayName: 'Invitation to set a password',
    mandatory: true,
    description:
      'HR added this person (or re-sent their sign-in link): a one-time link to set a password.',
    // `inviteUrl` is the destination (the set-password page), not an app route.
    requiredVariables: ['firstName', 'orgName', 'inviteUrl', 'expiresInDays'],
  },
  // ---- Organisation activation (ported from PR #231) ----------------------------------
  {
    key: 'org.activation_nudge_day1',
    module: 'org',
    displayName: 'Add your team (day 1)',
    description: "One day after signup, the owner's team is still not in the organisation.",
    href: '/admin/employee/add-team',
    requiredVariables: ['firstName', 'orgName', 'addTeamUrl', 'appName'],
  },
  {
    key: 'org.activation_nudge_day3',
    module: 'org',
    displayName: 'Add your team (day 3)',
    description: "Three days after signup, the owner's team is still not in the organisation.",
    href: '/admin/employee/add-team',
    requiredVariables: ['firstName', 'orgName', 'addTeamUrl', 'appName'],
  },
  // ---- Signup trial (apps/worker/src/handlers/org-trial-emails.ts), to the verified owner ----
  {
    key: 'org.trial_ending_soon',
    module: 'org',
    displayName: 'Trial ending soon',
    description: 'The free trial ends in a few days (sent once, about 4 days before the end).',
    href: '/admin/billing/plans',
    requiredVariables: ['firstName', 'orgName', 'daysLeft', 'trialEndDate', 'plansUrl'],
  },
  {
    key: 'org.trial_ends_tomorrow',
    module: 'org',
    displayName: 'Trial ends tomorrow',
    description: 'The free trial ends tomorrow (sent once, on the last full day of the trial).',
    href: '/admin/billing/plans',
    requiredVariables: ['firstName', 'orgName', 'endsWhen', 'trialEndDate', 'plansUrl'],
  },
  {
    key: 'org.trial_ended',
    module: 'org',
    displayName: 'Trial ended',
    description:
      'The free trial has ended and the workspace is read-only until a plan is chosen (sent once).',
    href: '/admin/billing/plans',
    requiredVariables: ['firstName', 'orgName', 'trialEndDate', 'plansUrl'],
  },
  {
    key: 'account.credentials_created',
    module: 'account',
    displayName: 'Login credentials created',
    description: 'HR created temporary login credentials for a new employee.',
    requiredVariables: ['firstName', 'orgName', 'loginEmail', 'temporaryPassword', 'loginUrl'],
  },

  // ---- Performance review ---------------------------------------------------------
  {
    key: 'review.nudge',
    module: 'review',
    displayName: 'Performance review reminder',
    description:
      "A reminder that a step in this person's performance review cycle is still waiting on them.",
    href: '/performance/reviews/{{reviewId}}',
    requiredVariables: ['cycleLabel', 'stageLabel', 'dueDate', 'appName'],
  },
  {
    key: 'review.manager_assessment_needed',
    module: 'review',
    displayName: 'Manager assessment ready',
    description:
      'A direct report finishes peer feedback and their review is ready for manager assessment.',
    href: '/performance/reviews/{{reviewId}}',
    requiredVariables: ['subjectName', 'cycleLabel', 'appName'],
  },
  {
    key: 'review.manager_reassigned',
    module: 'review',
    displayName: 'Review manager reassigned',
    description:
      "HR made this person the manager of record on a direct report's review mid-cycle; any manager assessment still to do is now theirs. Carries no score.",
    href: '/performance/reviews/{{reviewId}}',
    requiredVariables: ['subjectName', 'cycleLabel', 'appName'],
  },
  {
    key: 'review.assigned',
    module: 'review',
    displayName: 'Performance review assigned',
    description: 'A performance review is assigned to this person for a cycle.',
    href: '/performance/reviews/{{reviewId}}',
    requiredVariables: ['cycleLabel', 'dueDate', 'appName'],
  },
  {
    key: 'review.appeal.raised',
    module: 'review',
    displayName: 'Review appeal raised',
    description:
      'An employee disagreed with their finalized review (PRF-10) — sent to the resolved HR approver and the manager of record.',
    href: '/performance/reviews/{{reviewId}}',
    requiredVariables: ['subjectName', 'cycleLabel', 'appName'],
  },
  {
    key: 'review.appeal.decided',
    module: 'review',
    displayName: 'Review appeal decided',
    description: "HR's decision on an employee's review appeal.",
    href: '/performance/reviews/{{reviewId}}',
    requiredVariables: ['cycleLabel', 'decision', 'appName'],
  },

  // ---- Feedback, 1:1s and recognition -----------------------------------------
  // resolveHref() returns null (no link) if any placeholder is missing, so an optional id must
  // never sit in an href unless "no link" is an acceptable outcome; otherwise use the landing page.
  {
    key: 'feedback.request_received',
    module: 'feedback',
    displayName: 'Feedback requested from you',
    description: 'Someone asks this person for feedback on a colleague.',
    // No peer-feedback route exists in the web router and reviewId is optional: landing page.
    href: '/performance/feedback',
    requiredVariables: ['requesterName', 'subjectName', 'dueDate', 'appName'],
  },
  {
    key: 'feedback.request_submitted',
    module: 'feedback',
    displayName: 'Requested feedback submitted',
    description: 'A colleague submits feedback this person requested.',
    href: '/performance/feedback',
    requiredVariables: ['subjectName', 'appName'],
  },
  {
    key: 'feedback.received',
    module: 'feedback',
    displayName: 'Feedback received',
    description: 'A colleague shares feedback with this person.',
    href: '/performance/feedback',
    requiredVariables: ['authorLabel', 'appName'],
  },
  {
    key: 'feedback.nomination_submitted',
    module: 'feedback',
    displayName: 'Peer nomination submitted',
    description:
      "A direct report nominates peer reviewers for a cycle (PRF-13) — needs this manager's approval before requests go out.",
    href: '/performance/feedback',
    requiredVariables: ['subjectName', 'nomineeCount', 'appName'],
  },
  {
    key: 'feedback.nomination_decided',
    module: 'feedback',
    displayName: 'Peer nomination decided',
    description: "A manager's approve/reject decision on this person's own peer nomination.",
    href: '/performance/feedback',
    requiredVariables: ['decision', 'appName'],
  },
  // PRF-23: goal, improvement-plan and calibration notifications.
  {
    key: 'goal.submitted',
    module: 'goal',
    displayName: 'Goal submitted',
    description: 'An employee submitted a goal that needs manager approval.',
    href: '/performance/goals',
    requiredVariables: ['requesterName', 'goalTitle', 'appName'],
  },
  {
    key: 'goal.approved',
    module: 'goal',
    displayName: 'Goal approved',
    description: "A manager's approval decision on this employee's own goal.",
    href: '/performance/goals',
    requiredVariables: ['approverName', 'goalTitle', 'appName'],
  },
  {
    key: 'goal.rejected',
    module: 'goal',
    displayName: 'Goal rejected',
    description: "A manager's rejection decision on this employee's own goal.",
    href: '/performance/goals',
    requiredVariables: ['approverName', 'goalTitle', 'appName'],
  },
  {
    key: 'improvement_plan.created',
    module: 'improvement_plan',
    displayName: 'Improvement plan submitted',
    description: 'A new improvement plan was submitted into its approval workflow.',
    href: '/performance/reviews',
    requiredVariables: ['subjectName', 'appName'],
  },
  {
    key: 'improvement_plan.decided',
    module: 'improvement_plan',
    displayName: 'Improvement plan decided',
    description: "An approver's final approve/reject decision on an improvement plan.",
    href: '/performance/reviews',
    requiredVariables: ['deciderName', 'decision', 'appName'],
  },
  {
    // Performance remediation T4.5 / decision Q7: no automatic offboarding — HR and the manager are
    // told, and any separation is a separate, audited HR action.
    key: 'improvement_plan.unsuccessful',
    module: 'improvement_plan',
    displayName: 'Improvement plan closed unsuccessfully',
    description:
      'An improvement plan was closed as unsuccessful. Sent to HR and the manager; nothing is started automatically.',
    href: '/performance/reviews',
    requiredVariables: ['subjectName', 'closedByName', 'appName'],
  },
  {
    key: 'improvement_plan.milestone_due',
    module: 'improvement_plan',
    displayName: 'Improvement plan milestone due',
    description: "An improvement plan milestone's due date has arrived.",
    href: '/performance/reviews',
    requiredVariables: ['subjectName', 'milestoneTitle', 'appName'],
  },
  {
    key: 'goal.checkin_due',
    module: 'goal',
    displayName: 'Goal check-in reminder',
    description: 'An active goal has had no check-in for the cadence HR has set.',
    href: '/performance/goals',
    requiredVariables: ['goalTitle', 'daysSince', 'appName'],
  },
  {
    key: 'review.self_assessment_due',
    module: 'review',
    displayName: 'Self-review deadline reminder',
    description: 'A self-assessment deadline is tomorrow or today and it is not yet submitted.',
    href: '/performance/reviews/{{reviewId}}',
    requiredVariables: ['cycleName', 'dueOn', 'whenLabel', 'appName'],
  },
  {
    key: 'feedback.request_due',
    module: 'feedback',
    displayName: 'Feedback request reminder',
    description: 'A feedback request awaiting this person is due tomorrow or today.',
    href: '/performance/feedback',
    requiredVariables: ['subjectName', 'dueOn', 'whenLabel', 'appName'],
  },
  {
    key: 'one_on_one.reminder',
    module: 'one_on_one',
    displayName: '1:1 reminder',
    description: 'A scheduled 1:1 starts within the next day.',
    href: '/performance/feedback/one-on-ones/{{meetingId}}',
    requiredVariables: ['otherPersonName', 'scheduledAt', 'appName'],
  },
  {
    key: 'calibration.finalized',
    module: 'calibration',
    displayName: 'Calibration finalized',
    description: "A calibration session's HR-countersign workflow reached its terminal decision.",
    href: '/admin/performance/calibration',
    requiredVariables: ['appName'],
  },
  {
    key: 'one_on_one.scheduled',
    module: 'one_on_one',
    displayName: '1:1 scheduled',
    description: 'A 1:1 meeting is scheduled with this person.',
    href: '/performance/feedback/one-on-ones/{{meetingId}}',
    requiredVariables: ['otherPersonName', 'scheduledAt', 'appName'],
  },
  {
    key: 'one_on_one.action_item_assigned',
    module: 'one_on_one',
    displayName: '1:1 action item assigned',
    description: 'An action item from a 1:1 is assigned to this person.',
    // meetingId may be absent: then no link rather than a wrong one. Emitters should send it.
    href: '/performance/feedback/one-on-ones/{{meetingId}}',
    requiredVariables: ['assignerName', 'appName'],
  },
  {
    key: 'recognition.received',
    module: 'recognition',
    displayName: 'Recognition received',
    description: 'A colleague recognises this person, optionally with points.',
    href: '/engage/recognition?tab=received',
    requiredVariables: ['senderName', 'points', 'appName'],
  },
  {
    key: 'reward_redemption.approved',
    module: 'recognition',
    displayName: 'Reward redemption approved',
    description: "An admin approves this person's reward redemption request.",
    href: '/performance/feedback?tab=rewards',
    requiredVariables: ['itemName', 'deciderName', 'appName'],
  },
  {
    key: 'reward_redemption.cancelled',
    module: 'recognition',
    displayName: 'Reward redemption declined',
    description: "An admin declines this person's reward redemption request.",
    href: '/performance/feedback?tab=rewards',
    requiredVariables: ['itemName', 'deciderName', 'appName'],
  },
  {
    key: 'reward_redemption.fulfilled',
    module: 'recognition',
    displayName: 'Reward redemption fulfilled',
    description: "This person's reward redemption is fulfilled — points have been debited.",
    href: '/performance/feedback?tab=rewards',
    requiredVariables: ['itemName', 'appName'],
  },

  // ---- Performance insights ---------------------------------------------------
  {
    key: 'performance.attention.escalated',
    module: 'performance',
    displayName: 'Performance case escalated',
    description: "A person's overdue performance case escalates to the next attention level.",
    href: '/performance/reviews',
    requiredVariables: ['personName', 'levelLabel', 'daysOverdue', 'cycleLabel', 'appName'],
  },
  {
    key: 'performance.insights.export_ready',
    module: 'performance',
    displayName: 'Performance Insights export ready',
    description: 'A requested Performance Insights export finishes and is ready to download.',
    href: '/performance/insights?export={{jobId}}',
    requiredVariables: ['scopeLabel', 'jobId', 'expiresInHours', 'appName'],
  },
  {
    key: 'performance.merit_cycle.pending_review',
    module: 'performance',
    displayName: 'Merit round awaiting approval',
    description:
      "HR submitted a cycle's merit round for approval; sent to the approver whose step is next. Counts only, no amounts.",
    href: '/admin/performance/review-cycles/{{cycleId}}/roster?tab=merit',
    requiredVariables: ['cycleLabel', 'coveredCount', 'cycleId', 'appName'],
  },
  {
    key: 'account.company_access_granted',
    module: 'account',
    displayName: 'Access to more companies',
    mandatory: true,
    description:
      "A group owner gave this person access to more of the group's companies (ADR 0034) — a " +
      'security notice, unmutable, so nobody gains access to a company without being told.',
    requiredVariables: ['firstName', 'grantedBy', 'companies', 'signInUrl'],
  },
  {
    key: 'account.mfa_enabled',
    module: 'account',
    displayName: 'Two-factor authentication enabled',
    mandatory: true,
    description:
      'Two-factor authentication was turned on for this account — a security notice, unmutable.',
    requiredVariables: ['enabledAt', 'deviceLabel', 'supportEmail'],
  },
  {
    key: 'account.salary_pin_phone_added',
    module: 'account',
    displayName: 'Mobile number added and salary PIN reset',
    mandatory: true,
    description:
      'A mobile number was verified on this account and the salary PIN reset with it (ADR 0041) — a ' +
      'security notice, unmutable: the email is the one channel someone at an unlocked session does not hold.',
    requiredVariables: ['phoneEnding', 'changedAt'],
  },
  {
    key: 'account.salary_pin_reset_by_admin',
    module: 'account',
    displayName: 'Salary PIN reset by an administrator',
    mandatory: true,
    description:
      "An administrator cleared this person's salary PIN (ADR 0041); they set a new one next time — a " +
      'security notice, unmutable.',
    requiredVariables: ['resetBy', 'resetAt'],
  },

  // ---- Profile update -----------------------------------------------------------
  {
    key: 'profile_update.approved',
    module: 'profile_update',
    displayName: 'Profile update approved',
    description: "An employee's self-service profile change request is approved.",
    href: '/profile/update-requests/{{requestId}}',
    requiredVariables: ['category', 'reviewerNote'],
  },
  {
    key: 'profile_update.rejected',
    module: 'profile_update',
    displayName: 'Profile update declined',
    description: "An employee's self-service profile change request is declined.",
    href: '/profile/update-requests/{{requestId}}',
    requiredVariables: ['category', 'reviewerNote'],
  },

  // ---- Resignation / exit ---------------------------------------------------------
  // hrefs point at the employee's own /exit page (MyExitPage) — every key below is sent TO the
  // employee whose resignation/settlement it concerns, and that page shows their own single
  // active request, so no id param is needed. The previous /exit/resignation/{{id}} and
  // /exit/fnf-settlements/{{id}} hrefs pointed at web routes that never existed (404 on click) —
  // see offboarding-module-plan.md §1's "Two defects".
  {
    key: 'resignation.approved',
    module: 'resignation',
    displayName: 'Resignation approved',
    description: "An employee's resignation is approved, with a confirmed last working day.",
    href: '/exit',
    requiredVariables: ['lastWorkingDay', 'reviewerNote'],
  },
  {
    key: 'resignation.rejected',
    module: 'resignation',
    displayName: 'Resignation declined',
    description: "An employee's resignation request is declined.",
    href: '/exit',
    requiredVariables: ['rejectionNote'],
  },
  {
    key: 'resignation.revoke_approved',
    module: 'resignation',
    displayName: 'Resignation revocation approved',
    description:
      "An employee's request to withdraw their resignation is approved and they are reinstated.",
    href: '/exit/resignation/{{resignationId}}',
    requiredVariables: ['reviewerNote'],
  },
  {
    key: 'resignation.revoke_rejected',
    module: 'resignation',
    displayName: 'Resignation revocation declined',
    description:
      "An employee's request to withdraw their resignation is declined; the original resignation stands.",
    href: '/exit/resignation/{{resignationId}}',
    requiredVariables: ['rejectionNote'],
  },

  // ---- Transfer between legal entities -----------------------------------------
  {
    key: 'transfer.booked',
    module: 'transfer',
    displayName: 'Transfer to another company booked',
    description: 'HR moves an employee to another legal entity; sent to that employee.',
    href: '/',
    requiredVariables: ['oldCompanyName', 'newCompanyName', 'effectiveOn', 'newEmployeeCode'],
  },
  {
    key: 'transfer.team_joining',
    module: 'transfer',
    displayName: 'Someone is joining your team from another company',
    description:
      "An employee's transfer to another legal entity puts them under you; sent to the new manager.",
    href: '/admin/people/{{employmentId}}',
    requiredVariables: [
      'employeeName',
      'oldCompanyName',
      'newCompanyName',
      'effectiveOn',
      'employmentId',
    ],
  },
  {
    key: 'transfer.team_leaving',
    module: 'transfer',
    displayName: 'Someone on your team is moving to another company',
    description:
      "An employee's transfer to another legal entity takes them from you; sent to the old manager.",
    href: '/admin/people/{{employmentId}}',
    requiredVariables: [
      'employeeName',
      'oldCompanyName',
      'newCompanyName',
      'effectiveOn',
      'employmentId',
    ],
  },
  {
    key: 'transfer.cancelled',
    module: 'transfer',
    displayName: 'Transfer to another company cancelled',
    description: 'HR cancels a transfer that had not taken effect yet; sent to the employee.',
    href: '/',
    requiredVariables: ['newCompanyName', 'effectiveOn'],
  },

  // ---- Full & Final settlement -----------------------------------------------
  {
    key: 'fnf_settlement.approved',
    module: 'fnf_settlement',
    displayName: 'Full & Final settlement approved',
    description: "An exiting employee's Full & Final settlement is approved.",
    href: '/admin/payroll/operations/fnf?settlement={{settlementId}}',
    requiredVariables: ['netPayableAmount', 'currency', 'comment', 'settlementId'],
  },
  {
    key: 'fnf_settlement.rejected',
    module: 'fnf_settlement',
    displayName: 'Full & Final settlement declined',
    description:
      "An exiting employee's Full & Final settlement is declined and sent back for revision.",
    href: '/admin/payroll/operations/fnf?settlement={{settlementId}}',
    requiredVariables: ['netPayableAmount', 'currency', 'comment', 'settlementId'],
  },
  {
    key: 'fnf_settlement.paid',
    module: 'fnf_settlement',
    displayName: 'Full & Final settlement paid',
    description: "An exiting employee's Full & Final settlement is paid out.",
    href: '/admin/payroll/operations/fnf?settlement={{settlementId}}',
    requiredVariables: ['netPayableAmount', 'currency', 'settledOn', 'settlementId'],
  },
  {
    key: 'fnf_settlement.clearance_assigned',
    module: 'fnf_settlement',
    displayName: 'Exit clearance assigned',
    description: 'An exit clearance for a departing employee is assigned to its owner to record.',
    // The owner's own queue, open to anyone signed in: a reporting manager or IT owner has no
    // access to the settlement screen itself.
    href: '/team/exits/clearances',
    requiredVariables: ['employeeName', 'clearanceLabel', 'settlementId'],
  },
  {
    key: 'fnf_settlement.submitted',
    module: 'fnf_settlement',
    displayName: 'Full & Final settlement awaiting approval',
    description:
      'A calculated Full & Final settlement version is submitted for the owner to approve.',
    href: '/admin/payroll/operations/fnf?settlement={{settlementId}}',
    requiredVariables: ['employeeName', 'netPayableAmount', 'currency', 'settlementId'],
  },
  {
    key: 'fnf_settlement.statement_published',
    module: 'fnf_settlement',
    displayName: 'Full & Final statement published',
    description: "An exiting employee's settlement statement is published for them to read.",
    href: '/me/fnf/{{settlementId}}',
    requiredVariables: ['netPayableAmount', 'currency', 'settlementId'],
  },

  // ---- Offboarding pipeline ---------------------------------------------------
  {
    key: 'offboarding.process_started',
    module: 'offboarding',
    displayName: 'Offboarding started',
    description: 'An offboarding pipeline has started for a departing employee.',
    href: '/exit',
    requiredVariables: ['lastWorkingDay'],
  },
  {
    key: 'offboarding.task_assigned',
    module: 'offboarding',
    displayName: 'Offboarding task assigned',
    description: "A task on a departing employee's exit checklist was assigned to you.",
    href: '/team/exits/tasks',
    requiredVariables: ['taskTitle', 'dueDate'],
  },
  {
    key: 'offboarding.task_due',
    module: 'offboarding',
    displayName: 'Offboarding task due soon',
    description: 'A task on an exit checklist assigned to you is due soon.',
    href: '/team/exits/tasks',
    requiredVariables: ['taskTitle', 'dueDate'],
  },
  {
    key: 'offboarding.task_overdue',
    module: 'offboarding',
    displayName: 'Offboarding task overdue',
    description: 'A task on an exit checklist assigned to you is overdue.',
    href: '/team/exits/tasks',
    requiredVariables: ['taskTitle', 'dueDate'],
  },
  {
    key: 'offboarding.handover_assigned',
    module: 'offboarding',
    displayName: 'Handover assigned to you',
    description: "Work is being handed over to you as part of a colleague's exit.",
    href: '/team/exits/tasks',
    requiredVariables: ['fromName', 'taskTitle'],
  },
  {
    key: 'offboarding.exit_interview_requested',
    module: 'offboarding',
    displayName: 'Exit interview scheduled',
    description: 'Your exit interview is ready to complete.',
    href: '/exit/interview',
    requiredVariables: [],
  },
  {
    key: 'offboarding.asset_unreturned',
    module: 'offboarding',
    displayName: 'Asset not yet returned',
    description:
      'A company asset is still marked as not returned close to or past the last working day.',
    href: '/admin/offboarding/assets',
    requiredVariables: ['assetType', 'lastWorkingDay'],
  },
  {
    key: 'offboarding.lwd_approaching',
    module: 'offboarding',
    displayName: 'Last working day approaching',
    description: "A departing employee's last working day is approaching.",
    href: '/exit',
    requiredVariables: ['lastWorkingDay', 'daysRemaining'],
  },
  {
    key: 'offboarding.archived',
    module: 'offboarding',
    displayName: 'Offboarding completed',
    description: "An employee's offboarding process has been archived and their exit is complete.",
    href: '/admin/offboarding/pipeline',
    requiredVariables: [],
  },

  // ---- Requisition ----------------------------------------------------------------
  {
    key: 'requisition.approval_pending',
    module: 'requisition',
    displayName: 'Requisition awaiting approval',
    description: 'A headcount requisition is waiting for this approver to act on it.',
    href: '/recruitment/requisitions/{{requisitionId}}',
    requiredVariables: [
      'approverName',
      'requisitionTitle',
      'headcount',
      'requestedByName',
      'departmentName',
    ],
  },
  {
    key: 'requisition.approved',
    module: 'requisition',
    displayName: 'Requisition approved',
    description: "A recruiter's headcount requisition is approved.",
    href: '/recruitment/requisitions/{{requisitionId}}',
    requiredVariables: ['requisitionTitle'],
  },
  {
    key: 'requisition.rejected',
    module: 'requisition',
    displayName: 'Requisition declined',
    description: "A recruiter's headcount requisition is declined.",
    href: '/recruitment/requisitions/{{requisitionId}}',
    requiredVariables: ['requisitionTitle', 'comment'],
  },

  // ---- Interview ------------------------------------------------------------------
  {
    key: 'interview.scheduled',
    module: 'interview',
    displayName: 'Interview scheduled',
    description: 'An internal interviewer is scheduled to interview a candidate.',
    // The interviewer's own view (the old /recruitment/interviews/... path was never a page).
    href: '/my-interviews/{{interviewId}}',
    requiredVariables: [
      'interviewerName',
      'candidateName',
      'roundName',
      'scheduledAt',
      'meetingUrl',
    ],
  },
  {
    key: 'interview.feedback_due',
    module: 'interview',
    displayName: 'Interview feedback due',
    description: 'An interviewer has not filed their scorecard the day after the interview.',
    href: '/my-interviews/{{interviewId}}',
    requiredVariables: ['interviewerName', 'candidateName', 'roundName', 'scheduledAt'],
  },

  {
    key: 'interview.reminder_candidate',
    module: 'interview',
    displayName: 'Interview reminder (candidate)',
    external: true,
    description: 'Reminds a candidate on the morning of their interview, with the join link.',
    requiredVariables: [
      'candidateFirstName',
      'postingTitle',
      'roundName',
      'orgName',
      'scheduledAt',
      'meetingUrl',
    ],
  },
  {
    key: 'interview.reminder_panel',
    module: 'interview',
    displayName: 'Interview reminder (interviewer)',
    description: 'Reminds an interviewer on the morning of an interview they are on.',
    href: '/my-interviews/{{interviewId}}',
    requiredVariables: [
      'interviewerName',
      'candidateName',
      'roundName',
      'scheduledAt',
      'meetingUrl',
    ],
  },
  {
    key: 'offer.join_overdue',
    module: 'offer',
    displayName: 'Candidate has not joined',
    description:
      "Tells the hiring team that an accepted offer's joining date passed with no start.",
    href: '/admin/recruitment/offers',
    requiredVariables: ['candidateName', 'postingTitle', 'joinDate'],
  },
  {
    key: 'recruitment.sla_digest',
    module: 'candidate',
    displayName: 'Weekly hiring digest',
    description:
      'Each Monday: candidates waiting, scorecards owed, offers expiring and old requisitions.',
    href: '/admin/recruitment/candidates',
    requiredVariables: [
      'total',
      'stuckCandidates',
      'scorecardsOverdue',
      'offersExpiring',
      'requisitionsAged',
    ],
  },

  // ---- Offer (internal approver) ---------------------------------------------
  {
    key: 'offer.approval_pending',
    module: 'offer',
    displayName: 'Offer awaiting approval',
    description: 'A candidate offer is waiting for this approver to act on it.',
    href: '/recruitment/offers/{{offerId}}',
    requiredVariables: ['approverName', 'candidateName', 'postingTitle', 'annualCtc', 'currency'],
  },
  {
    key: 'offer.approved',
    module: 'offer',
    displayName: 'Offer approved',
    description: 'A candidate offer is approved and ready to send.',
    href: '/recruitment/offers/{{offerId}}',
    requiredVariables: ['candidateName', 'postingTitle'],
  },
  {
    key: 'offer.accepted',
    module: 'offer',
    displayName: 'Offer accepted',
    description: 'A candidate accepted their offer; told to the people who hire for the role.',
    href: '/recruitment/offers/{{offerId}}',
    requiredVariables: ['recipientName', 'candidateName', 'postingTitle', 'joinDate'],
  },
  {
    key: 'offer.declined',
    module: 'offer',
    displayName: 'Offer declined',
    description: 'A candidate declined their offer; told to the people who hire for the role.',
    href: '/recruitment/offers/{{offerId}}',
    requiredVariables: ['recipientName', 'candidateName', 'postingTitle'],
  },

  // ---- Candidate correspondence (external, no Person record) -----------------
  {
    key: 'candidate.application_received',
    module: 'candidate',
    displayName: 'Application received',
    external: true,
    description: 'Sent to a candidate confirming their job application was received.',
    // External correspondence to someone with no employment relationship and no in-app inbox —
    // there is no internal route to link them to.
    requiredVariables: [
      'candidateFirstName',
      'postingTitle',
      'orgName',
      'applicationRef',
      'statusUrl',
    ],
  },
  {
    key: 'candidate.interview_scheduled',
    module: 'candidate',
    displayName: 'Interview scheduled (candidate)',
    external: true,
    description: 'Sent to a candidate confirming their interview date and joining link.',
    requiredVariables: ['candidateFirstName', 'roundName', 'scheduledAt', 'meetingUrl', 'orgName'],
  },
  {
    key: 'candidate.interview_cancelled',
    module: 'candidate',
    displayName: 'Interview cancelled (candidate)',
    external: true,
    description: 'Sent to a candidate when their booked interview is cancelled.',
    requiredVariables: ['candidateFirstName', 'roundName', 'scheduledAt', 'orgName'],
  },

  // ---- Offer (external candidate) ---------------------------------------------
  {
    key: 'offer.sent',
    module: 'offer',
    displayName: 'Offer sent',
    external: true,
    description: 'A signed offer letter is sent to a candidate.',
    // `offerUrl` is the destination, already a full link the candidate — who has no HRMS account —
    // opens directly; not an in-app route.
    requiredVariables: [
      'candidateFirstName',
      'postingTitle',
      'annualCtc',
      'currency',
      'targetJoinDate',
      'offerUrl',
      'orgName',
    ],
  },
  {
    key: 'offer.accepted_confirmation',
    module: 'offer',
    displayName: 'Offer accepted',
    external: true,
    description: 'Sent to a candidate confirming their accepted offer and target join date.',
    requiredVariables: ['candidateFirstName', 'orgName', 'targetJoinDate'],
  },
  {
    key: 'candidate.slot_offer',
    module: 'candidate',
    displayName: 'Pick an interview time',
    external: true,
    description:
      'Sends a candidate the link to choose their own interview time from the ones offered.',
    requiredVariables: [
      'candidateFirstName',
      'postingTitle',
      'roundName',
      'slotUrl',
      'expiresOn',
      'orgName',
    ],
  },
  {
    key: 'candidate.assessment_sent',
    module: 'candidate',
    displayName: 'Assessment sent',
    description:
      'A candidate is sent a link to complete an assessment as part of their application.',
    requiredVariables: ['candidateFirstName', 'templateName', 'assessmentUrl', 'orgName'],
  },
  {
    key: 'offer.expiring',
    module: 'offer',
    displayName: 'Offer expiring soon',
    external: true,
    description:
      'Reminds a candidate that their offer is about to expire (the link is in the original email).',
    requiredVariables: ['candidateFirstName', 'postingTitle', 'orgName', 'expiresOn'],
  },
  {
    key: 'job.internal_application_received',
    module: 'candidate',
    displayName: 'Internal application received',
    description:
      'Confirms to an employee that their application to an internal posting was received.',
    requiredVariables: ['postingTitle'],
  },
  {
    key: 'job.internal_application_manager',
    module: 'candidate',
    displayName: 'Team member applied internally',
    description:
      'Tells a reporting manager that someone on their team applied to an internal posting.',
    requiredVariables: ['applicantName', 'postingTitle', 'departmentName'],
  },
  {
    key: 'referral.submitted',
    module: 'candidate',
    displayName: 'Referral received',
    description: 'Confirms to an employee that the candidate they referred was received.',
    requiredVariables: ['candidateName', 'postingTitle'],
  },
  {
    key: 'referral.received',
    module: 'candidate',
    displayName: 'New employee referral',
    description: "Tells a posting's hiring manager that an employee referred a candidate.",
    href: '/admin/recruitment/candidates/{{candidateId}}',
    requiredVariables: ['referrerName', 'candidateName', 'postingTitle'],
  },
  {
    key: 'candidate.agency_submitted',
    module: 'candidate',
    displayName: 'Agency submitted a candidate',
    description:
      "Tells a role's recruiters and hiring manager that a recruitment agency put someone forward.",
    href: '/admin/recruitment/candidates/{{candidateId}}',
    requiredVariables: ['agencyName', 'candidateName', 'postingTitle'],
  },
  {
    key: 'candidate.message',
    module: 'candidate',
    displayName: 'Message to a candidate',
    external: true,
    description:
      'An email a recruiter sends a candidate from the app (including rejection emails).',
    requiredVariables: ['subject', 'bodyHtml'],
  },
  {
    key: 'candidate.note_mention',
    module: 'candidate',
    displayName: 'Mentioned on a candidate',
    description: 'A colleague @mentioned you in a note on a candidate.',
    href: '/admin/recruitment/candidates/{{candidateId}}',
    requiredVariables: ['authorName', 'candidateName', 'excerpt'],
  },
  {
    key: 'candidate.hired',
    module: 'candidate',
    displayName: 'Hired',
    description: 'A candidate is marked as hired for the role they applied to.',
    requiredVariables: ['candidateFirstName', 'postingTitle', 'orgName'],
  },

  // ---- Onboarding -----------------------------------------------------------------
  {
    key: 'onboarding.nudge',
    module: 'onboarding',
    displayName: 'Onboarding reminder',
    description: "A general reminder from the People team about a new joiner's onboarding journey.",
    // A generic nudge, not about one specific task — see onboarding.task_overdue for that case.
    requiredVariables: ['firstName'],
  },
  {
    key: 'onboarding.task_overdue',
    module: 'onboarding',
    displayName: 'Onboarding task overdue',
    description: 'A specific onboarding task passed its due date without being completed.',
    // Static: the worker sends no taskId, and every recipient (hire, HR, manager, buddy, IT) holds
    // onboarding:read, which gates /my-onboarding.
    href: '/my-onboarding',
    requiredVariables: ['taskTitle', 'dueDate', 'period'],
  },
  {
    key: 'onboarding.welcome_note',
    module: 'onboarding',
    displayName: 'Welcome note',
    description:
      'HR sends a new hire a personal welcome message by completing the "Send welcome note" onboarding task.',
    // No href: this is a free-text message, not a link to a record — the employee bell shows the
    // full body inline, same as any other notification with no single destination screen.
    requiredVariables: ['message', 'senderName'],
  },
  {
    key: 'onboarding.task_assigned',
    module: 'onboarding',
    displayName: 'Onboarding tasks assigned',
    description:
      'Onboarding tasks land on a manager, buddy, IT owner or named person (one digest per person per hire).',
    requiredVariables: ['employeeName', 'taskCount', 'firstTaskTitle', 'earliestDueDate', 'role'],
  },
  {
    key: 'onboarding.task_completed',
    module: 'onboarding',
    displayName: 'Task completed',
    description: 'An employee completes an onboarding task.',
    requiredVariables: ['taskTitle', 'employeeName'],
  },
  {
    key: 'onboarding.probation_extended',
    module: 'onboarding',
    displayName: 'Probation extended',
    description:
      "HR pushes out an employee's probation end date, sent to the employee and their reporting manager.",
    // No href: the probation panel lives at /admin/onboarding (OnboardingProbationPage), which needs
    // onboarding:manage, and the employee recipient does not hold it. A link that 403s for one of
    // the two recipients is worse than none.
    requiredVariables: ['employeeName', 'newProbationEndOn', 'reason', 'extendedByName'],
  },
  {
    key: 'onboarding.probation_extension_rejected',
    module: 'onboarding',
    displayName: 'Probation extension rejected',
    description:
      'An approver rejects a probation extension request, sent to the person who requested it.',
    // No href, same reason as onboarding.probation_extended above.
    requiredVariables: ['employeeName', 'requestedProbationEndOn'],
  },
  {
    key: 'onboarding.probation_confirmed',
    module: 'onboarding',
    displayName: 'Probation confirmed',
    description:
      'An employee is confirmed after probation, sent to the employee and their reporting manager.',
    // No href, same reason as onboarding.probation_extended above.
    requiredVariables: ['employeeName', 'confirmedOn'],
  },
  {
    key: 'onboarding.probation_ending_soon',
    module: 'onboarding',
    displayName: 'Probation ending soon',
    description:
      "An employee's probation period ends within 15 days, sent to the employee, their reporting " +
      'manager and the HR who may confirm it (apps/worker probation.review_due daily sweep).',
    // No href, same reason as onboarding.probation_extended above.
    requiredVariables: ['employeeName', 'probationEndOn', 'daysRemaining'],
  },
  {
    key: 'onboarding.task_due_soon',
    module: 'onboarding',
    displayName: 'Onboarding task due soon',
    description:
      'A specific onboarding task is due within the next few days and is still open (worker sweep, once per task per due date).',
    // Same static href as onboarding.task_overdue, for the same reason.
    href: '/my-onboarding',
    requiredVariables: ['taskTitle', 'dueDate', 'daysRemaining'],
  },

  // ---- Document tasks (HR-assigned reads/signatures, independent of onboarding) ----
  {
    key: 'document_task.assigned',
    module: 'document_task',
    displayName: 'Document assigned',
    description: 'HR assigns a person a document to read, and optionally sign and return.',
    href: '/my-onboarding',
    requiredVariables: ['title', 'dueDate', 'requiresSignature'],
  },
  {
    key: 'document_task.completed',
    module: 'document_task',
    displayName: 'Document task completed',
    description:
      'A person completes a document task HR assigned them, notifying whoever assigned it.',
    requiredVariables: ['title', 'employeeName'],
  },

  // ---- AI copilot ---------------------------------------------------------------
  {
    key: 'ai.allowance_warning',
    module: 'ai',
    displayName: 'AI allowance running low',
    description: "An organisation's AI copilot allowance for the period crosses a usage threshold.",
    requiredVariables: [
      'orgName',
      'percentUsed',
      'periodKind',
      'consumedTokens',
      'includedTokens',
      'periodEnd',
      'appName',
    ],
  },
  {
    key: 'ai.addon_requested',
    module: 'ai',
    displayName: 'AI add-on pack requested',
    description: 'An admin requests an add-on pack of AI copilot tokens.',
    requiredVariables: ['requesterName', 'orgName', 'tokens', 'note', 'requestId'],
  },

  // ---- Chat -------------------------------------------------------------------
  {
    key: 'chat.message',
    module: 'chat',
    displayName: 'New chat message',
    description:
      'A debounced push for unread messages in a conversation the person has not read yet.',
    href: '/chat/{{conversationId}}',
    requiredVariables: ['unread'],
  },
  {
    key: 'chat.mention',
    module: 'chat',
    displayName: 'You were mentioned',
    description:
      'Someone @mentioned this person in a chat message — sent immediately, bypassing conversation mute (unlike the debounced chat.message push above).',
    href: '/chat/{{conversationId}}',
    requiredVariables: ['senderName'],
  },
  {
    key: 'chat.reaction',
    module: 'chat',
    displayName: 'Someone reacted to your message',
    description:
      'Someone reacts to a message this person sent, and it is a new reaction rather than a removal — unlike chat.mention above, this respects the conversation mute.',
    href: '/chat/{{conversationId}}',
    requiredVariables: ['reactorName', 'emoji'],
  },
  {
    key: 'chat.dlp_flagged',
    module: 'chat',
    displayName: 'Chat message flagged for review',
    description:
      "A chat message is automatically redacted for containing sensitive data, notifying the org's HR-admin-equivalent role-holders (never the sender).",
    href: '/chat/{{conversationId}}',
    requiredVariables: ['senderName', 'rulesLabel'],
  },

  // ---- Timesheets, work plans, projects -----------------------------------------
  {
    key: 'attendance.late_arrival',
    module: 'attendance',
    displayName: 'Late arrival recorded',
    description:
      'An employee clocks in after the shift grace period. Sent to HR. Email is off by default: it is one email ' +
      'per late employee, and the start-of-shift summary already lists who is late.',
    defaultDisabledChannels: ['email'],
    href: '/attendance',
    requiredVariables: [
      'employeeName',
      'date',
      'shiftLabel',
      'scheduledStartTime',
      'actualClockInTime',
      'lateMinutes',
      'appName',
      'employeeCode',
      'department',
      'designation',
      'locationName',
      'reportingManager',
      'cardHtml',
      'openUrl',
    ],
  },
  {
    key: 'attendance.late_arrival_self',
    module: 'attendance',
    displayName: 'Your late check-in recorded',
    description:
      'An employee clocks in after the shift grace period. Sent to the employee themselves.',
    href: '/attendance',
    requiredVariables: [
      'employeeName',
      'date',
      'shiftLabel',
      'scheduledStartTime',
      'actualClockInTime',
      'lateMinutes',
      'appName',
      'cardHtml',
      'openUrl',
    ],
  },
  {
    key: 'attendance.start_summary',
    module: 'attendance',
    displayName: 'Check-in status summary',
    description:
      "Sent to HR and to reporting managers when a shift's grace period ends: who has checked in, who is late, who has not checked in, and who is on leave or on duty. Covers only the people each recipient oversees.",
    href: '/attendance',
    requiredVariables: [
      'recipientName',
      'date',
      'shiftLabel',
      'cutoffTime',
      'totalCount',
      'presentCount',
      'presentList',
      'lateCount',
      'lateList',
      'notCheckedInCount',
      'notCheckedInList',
      'onLeaveCount',
      'onLeaveList',
      'onDutyCount',
      'onDutyList',
      'appName',
      'notCheckedInDetails',
      'lateDetails',
      'presentDetails',
      'onLeaveDetails',
      'onDutyDetails',
      'cardHtml',
      'openUrl',
    ],
  },
  {
    key: 'attendance.shift_reminder',
    module: 'attendance',
    displayName: 'Shift starting soon',
    description:
      'Sent to an employee before their shift begins, as a check-in reminder. Timing is set per company (default 15 minutes).',
    href: '/attendance',
    requiredVariables: [
      'employeeName',
      'shiftName',
      'shiftStartTime',
      'shiftEndTime',
      'date',
      'leadMinutes',
      'appName',
      'cardHtml',
      'openUrl',
    ],
  },
  {
    key: 'attendance.clock_out_reminder',
    module: 'attendance',
    displayName: 'Shift ending soon — clock out reminder',
    description:
      'Sent to an employee before their shift ends, reminding them to clock out. Timing is set per company (default 15 minutes).',
    href: '/attendance',
    requiredVariables: [
      'employeeName',
      'shiftName',
      'shiftEndTime',
      'checkInTime',
      'date',
      'appName',
      'cardHtml',
      'openUrl',
    ],
  },
  {
    key: 'attendance.auto_clock_out',
    module: 'attendance',
    displayName: 'Auto clocked out',
    description:
      'Sent to an employee whose forgotten clock-out was force-closed by the auto-clock-out sweep (Shift.autoClockOutMinutes, opt-in per shift).',
    href: '/attendance',
    requiredVariables: ['date', 'clockOutTime'],
  },
  {
    key: 'attendance.check_in_missed',
    module: 'attendance',
    displayName: "You haven't checked in",
    description:
      "Sent to an employee when their shift's grace period has passed with no check-in (and they are not on leave, a holiday, a weekly off or an approved on-duty/remote day). Timing is set per company.",
    href: '/attendance',
    requiredVariables: [
      'employeeName',
      'shiftName',
      'shiftStartTime',
      'date',
      'appName',
      'cardHtml',
      'openUrl',
    ],
  },
  {
    key: 'attendance.clock_out_missed',
    module: 'attendance',
    displayName: "You're still clocked in",
    description:
      'Sent to an employee when their shift has ended and they have not clocked out. Timing is set per company.',
    href: '/attendance',
    requiredVariables: [
      'employeeName',
      'shiftName',
      'shiftEndTime',
      'checkInTime',
      'date',
      'appName',
      'cardHtml',
      'openUrl',
    ],
  },
  {
    key: 'attendance.checked_in_with_plan',
    module: 'attendance',
    displayName: "Checked in, with today's plan",
    description:
      "Sent when an employee checks in for the day: the check-in time, on time or late, and the tasks they entered on the check-in form. One email per employee per day. Off until a company switches it on; recipients are chosen under 'Who receives these emails'.",
    href: '/projects/task-approvals',
    requiredVariables: [
      'employeeName',
      'checkInTime',
      'date',
      'punctuality',
      'locationName',
      'taskCount',
      'taskTableHtml',
      'appName',
    ],
    // Opt-in on every channel: a check-in email per employee is noise for most companies.
    defaultDisabledChannels: ['email', 'in_app', 'push', 'web_push', 'sms', 'whatsapp', 'slack'],
  },
  {
    key: 'attendance.early_exit_warning',
    module: 'attendance',
    displayName: 'Early exit warning',
    description:
      'An employee clocks out past the shift’s early-exit warning threshold, short of a penalty tier.',
    href: '/attendance/early-out',
    requiredVariables: ['date', 'earlyMinutes', 'shiftLabel'],
  },
  {
    key: 'attendance.early_exit_waiver_approved',
    module: 'attendance',
    displayName: 'Early-exit waiver approved',
    description: 'A manager or HR approves an employee’s early-exit waiver request.',
    href: '/attendance/early-out',
    requiredVariables: ['approverName', 'date', 'remarks'],
  },
  {
    key: 'attendance.early_exit_waiver_rejected',
    module: 'attendance',
    displayName: 'Early-exit waiver rejected',
    description: 'A manager or HR rejects an employee’s early-exit waiver request.',
    href: '/attendance/early-out',
    requiredVariables: ['approverName', 'date', 'remarks'],
  },
  {
    key: 'attendance.clock_out_override_requested',
    module: 'attendance',
    displayName: 'Clock-out override requested',
    description:
      'An employee asks to clock out despite the gate blocking them (ADR 0020 §1), notifying their manager.',
    href: '/attendance/my',
    requiredVariables: ['employeeName', 'date', 'reasonSummary', 'appName'],
  },
  {
    key: 'attendance.clock_out_override_decided',
    module: 'attendance',
    displayName: 'Clock-out override decided',
    description:
      'A manager approves or rejects a clock-out override request, notifying the requester.',
    href: '/attendance/my',
    requiredVariables: ['decision', 'decidedByName', 'date', 'appName'],
  },
  {
    key: 'attendance.day_verified',
    module: 'attendance',
    displayName: 'Attendance corrected by a manager',
    description: "A manager or HR manually sets the status of an employee's attendance day.",
    href: '/attendance',
    requiredVariables: ['date', 'statusLabel', 'actorName', 'remarks'],
  },
  {
    key: 'shift.roster_changed',
    module: 'shift',
    displayName: 'Published roster entry removed',
    description:
      'A roster manager removes a day from a roster the employee had already been shown.',
    href: '/attendance',
    requiredVariables: ['date'],
  },
  {
    key: 'attendance.absent_marked',
    module: 'attendance',
    displayName: 'Marked absent',
    description:
      'A finished working day closes with no attendance, so the employee can regularise it.',
    href: '/attendance',
    requiredVariables: ['date', 'cardHtml', 'openUrl'],
  },
  {
    key: 'attendance.missed_punch',
    module: 'attendance',
    displayName: 'Missed punch',
    description:
      'A finished working day has a clock-in but no clock-out, so the employee can regularise it.',
    href: '/attendance',
    requiredVariables: ['date', 'cardHtml', 'openUrl'],
  },
  {
    key: 'attendance.team_exceptions',
    module: 'attendance',
    displayName: 'Team attendance exceptions',
    description: "A manager's daily summary of direct reports who were absent or missed a punch.",
    href: '/attendance',
    requiredVariables: [
      'absentCount',
      'missedPunchCount',
      'absentDetails',
      'missedPunchDetails',
      'cardHtml',
      'openUrl',
    ],
    // Off by default (attendance emails plan, 2026-09-27): companies get the morning digest only.
    defaultDisabledChannels: ['email'],
  },
  {
    key: 'approval.overdue',
    module: 'approval',
    displayName: 'Approval overdue',
    description: 'A request has waited on its approver longer than the reminder threshold.',
    requiredVariables: ['requesterName', 'itemType', 'waitingHours'],
  },
  {
    key: 'approval.escalated',
    module: 'approval',
    displayName: 'Approval stuck — HR notice',
    description:
      'A request has waited on its approver far past the reminder threshold, so HR is told.',
    requiredVariables: ['requesterName', 'itemType', 'waitingHours', 'approverName'],
  },
  {
    key: 'attendance.request_submitted',
    module: 'attendance',
    displayName: 'Attendance request submitted',
    description:
      'Confirms to the person the request is about that it was filed and is now with their approver.',
    href: '/attendance',
    requiredVariables: ['itemType', 'date'],
  },
  {
    key: 'attendance.cycle_frozen',
    module: 'attendance',
    displayName: 'Attendance cycle locked',
    description:
      'HR locks a month of attendance, after which regularisations and shift changes for it are refused.',
    href: '/attendance',
    requiredVariables: ['periodLabel'],
  },
  {
    key: 'shift.assigned',
    module: 'shift',
    displayName: 'Shift assigned',
    description: 'HR or a roster manager assigns an employee to a shift.',
    href: '/attendance',
    requiredVariables: ['shiftLabel', 'effectiveFrom'],
  },
  {
    key: 'shift.roster_published',
    module: 'shift',
    displayName: 'Roster published',
    description: "A roster covering an employee's days is published.",
    href: '/attendance',
    requiredVariables: ['startDate', 'endDate'],
  },
  {
    key: 'timesheet.submission_due',
    module: 'timesheet',
    displayName: 'Timesheet due',
    description: 'A timesheet for a closed period is due for submission.',
    href: '/projects/timesheet',
    requiredVariables: ['periodLabel', 'dueDate', 'appName'],
  },
  {
    key: 'timesheet.overdue',
    module: 'timesheet',
    displayName: 'Timesheet overdue',
    description: 'A timesheet is still a draft past its submission due date.',
    href: '/projects/timesheet',
    requiredVariables: ['periodLabel', 'dueDate', 'appName'],
  },
  {
    key: 'timesheet.submitted',
    module: 'timesheet',
    displayName: 'Timesheet submitted',
    description: 'An employee submits a timesheet that needs their approver to act on it.',
    href: '/projects/approvals',
    requiredVariables: ['requesterName', 'periodLabel', 'totalHours', 'appName'],
  },
  {
    key: 'timesheet.approved',
    module: 'timesheet',
    displayName: 'Timesheet approved',
    description: "An employee's timesheet is approved.",
    href: '/projects/timesheet',
    requiredVariables: ['approverName', 'periodLabel', 'totalHours'],
  },
  {
    key: 'timesheet.rejected',
    module: 'timesheet',
    displayName: 'Timesheet declined',
    description: "An employee's timesheet is declined.",
    href: '/projects/timesheet',
    requiredVariables: ['approverName', 'periodLabel', 'reason'],
  },
  {
    key: 'timesheet.auto_locked',
    module: 'timesheet',
    displayName: 'Timesheet auto-locked',
    description: 'An approved timesheet is locked automatically after the lock window.',
    href: '/projects/timesheet',
    requiredVariables: ['periodLabel', 'dueDate', 'totalHours'],
  },
  {
    key: 'work_plan.missing',
    module: 'work_plan',
    displayName: 'Daily work plan needed',
    description: 'An employee has not submitted a daily work plan.',
    href: '/projects/work-plan',
    requiredVariables: ['date', 'appName'],
  },
  {
    key: 'work_plan.task_submitted',
    module: 'work_plan',
    displayName: 'Work plan task to review',
    description: 'An employee proposes a task on their check-in form that needs approval.',
    href: '/projects/task-approvals',
    requiredVariables: ['employeeName', 'taskTitle', 'projectName'],
  },
  {
    key: 'work_plan.task_approved',
    module: 'work_plan',
    displayName: 'Work plan task approved',
    description: 'A task proposed at check-in is approved.',
    href: '/projects/work-plan',
    requiredVariables: ['approverName', 'taskTitle', 'projectName'],
  },
  {
    key: 'work_plan.task_rejected',
    module: 'work_plan',
    displayName: 'Work plan task rejected',
    description: 'A task proposed at check-in is rejected.',
    href: '/projects/work-plan',
    requiredVariables: ['approverName', 'taskTitle', 'projectName', 'reason'],
  },
  {
    key: 'project.member_added',
    module: 'project',
    displayName: 'Added to a project',
    description: 'A person is added to a project team.',
    href: '/projects/my',
    requiredVariables: ['projectName', 'addedByName', 'roleLabel', 'startDate', 'appName'],
  },
  {
    key: 'project.task_assigned',
    module: 'project',
    displayName: 'Task assigned',
    description: 'A project task is assigned to a person.',
    href: '/projects/tasks',
    requiredVariables: ['taskTitle', 'projectName', 'assignedByName', 'dueDate', 'appName'],
  },
  {
    key: 'project.task_due',
    module: 'project',
    displayName: 'Task due soon',
    description: 'A project task assigned to a person is about to fall due.',
    href: '/projects/tasks',
    requiredVariables: ['taskTitle', 'projectName', 'dueDate', 'appName'],
  },
  {
    key: 'project.task_overdue_escalation',
    module: 'project',
    displayName: 'Task overdue escalation',
    description:
      'An open task is several days overdue with no extension requested, notifying the project owner and managers (repeats weekly while it stays overdue).',
    href: '/projects/tasks',
    requiredVariables: [
      'taskTitle',
      'projectName',
      'assigneeName',
      'daysOverdue',
      'dueDate',
      'appName',
    ],
  },
  {
    key: 'project.task_comment',
    module: 'project',
    displayName: 'Task comment',
    description: 'Someone comments on a task, notifying the assignee or the project owner.',
    href: '/projects/tasks',
    requiredVariables: ['taskTitle', 'projectName', 'authorName', 'commentPreview', 'appName'],
  },
  {
    key: 'project.task_reopened',
    module: 'project',
    displayName: 'Task reopened',
    description: 'A manager reopens a finished task, notifying its assignee.',
    href: '/projects/tasks',
    requiredVariables: ['taskTitle', 'projectName', 'reopenedByName', 'reason', 'appName'],
  },
  {
    key: 'project.task_review_requested',
    module: 'project',
    displayName: 'Task ready for review',
    description: 'An employee finishes a task on a project that requires completion review.',
    href: '/projects/tasks',
    requiredVariables: ['taskTitle', 'projectName', 'employeeName', 'appName'],
  },
  {
    key: 'project.task_review_rejected',
    module: 'project',
    displayName: 'Task sent back from review',
    description:
      'A manager reviews a task and sends it back with a reason, notifying its assignee (ADR 0020 §2).',
    href: '/projects/tasks',
    requiredVariables: ['taskTitle', 'projectName', 'rejectedByName', 'reason', 'appName'],
  },
  {
    key: 'project.task_completed',
    module: 'project',
    displayName: 'Task completed',
    description: 'A task is marked done, notifying the project owner/managers (ADR 0020 §3).',
    href: '/projects/tasks',
    requiredVariables: ['taskTitle', 'projectName', 'completedByName', 'appName'],
  },
  {
    key: 'project.task_progress_digest',
    module: 'project',
    displayName: "Yesterday's task progress",
    description:
      "A daily, informational-only digest of what changed on the employee's tasks the previous day (ADR 0020 §5) — never blocks anything.",
    href: '/projects/tasks',
    requiredVariables: ['date', 'appName'],
  },
  {
    key: 'project.task_extension_requested',
    module: 'project',
    displayName: 'Task extension requested',
    description: 'An employee asks for more time on a task, notifying the approver.',
    href: '/projects/tasks',
    requiredVariables: [
      'taskTitle',
      'projectName',
      'employeeName',
      'currentDueDate',
      'requestedDueDate',
      'reason',
      'appName',
    ],
  },
  {
    key: 'project.task_extension_decided',
    module: 'project',
    displayName: 'Task extension decided',
    description: 'A manager approves or rejects a task extension request.',
    href: '/projects/tasks',
    requiredVariables: [
      'taskTitle',
      'projectName',
      'decision',
      'decidedByName',
      'newDueDate',
      'appName',
    ],
  },
  {
    key: 'project.task_reminder_morning',
    module: 'project',
    displayName: 'Morning task reminder',
    description: 'A scheduled morning reminder of the open tasks a person should work on today.',
    href: '/projects/tasks',
    requiredVariables: ['date', 'taskCount', 'taskList', 'appName'],
  },
  {
    key: 'project.task_reminder_evening',
    module: 'project',
    displayName: 'Evening task reminder',
    description: 'A scheduled evening reminder of the tasks still open today.',
    href: '/projects/tasks',
    requiredVariables: ['date', 'taskCount', 'taskList', 'appName'],
  },
  {
    key: 'project.milestone_due',
    module: 'project',
    displayName: 'Milestone due soon',
    description: 'A project milestone is about to fall due.',
    href: '/projects/my',
    requiredVariables: ['milestoneName', 'projectName', 'dueDate', 'appName'],
  },
  {
    key: 'project.allocation_changed',
    module: 'project',
    displayName: 'Project allocation changed',
    description: "A person's allocation on a project changes.",
    href: '/projects/my',
    requiredVariables: [
      'projectName',
      'allocationPercent',
      'startDate',
      'changedByName',
      'appName',
    ],
  },
  {
    // Daily Work Planning Phase 10 (T10.1) — carryForwardTask (project.ts) reuses the SAME
    // ProjectTask row (dueDate -> today, originKind -> carried_forward); the task's assignee is
    // told, not the manager who carried it, mirroring project.task_assigned's own "tell the
    // person the work now belongs to" rule.
    key: 'project.work_plan_item_carried_forward',
    module: 'project',
    displayName: 'Work plan item carried forward',
    description: 'A manager carries an open task forward to today, notifying its assignee.',
    href: '/projects/work-plan',
    requiredVariables: ['taskTitle', 'projectName', 'carriedForwardByName', 'dueDate', 'appName'],
  },
  {
    // Daily Work Planning Phase 10 (T10.1) — createEmployeeTarget (project.ts, org.settings:update
    // only). Tells the targeted employee, not the HR admin who created it.
    key: 'project.employee_target_assigned',
    module: 'project',
    displayName: 'Target assigned',
    description:
      'HR assigns a measurable performance target, against an existing KPI, to an employee.',
    href: '/projects/work-performance',
    requiredVariables: ['kpiName', 'targetValue', 'unit', 'startDate', 'endDate', 'appName'],
  },
  {
    // Daily Work Planning Phase 10 (T10.3) — scheduled worker sweep (apps/worker/src/workers/
    // project.ts), per legal entity, same shape as project.task_due/escalateOverdueTasks. Tells
    // only the targeted employee (project.task_due's own single-recipient convention, not the
    // leadership fan-out project.task_overdue_escalation uses) once their EmployeeTarget's endDate
    // is within the lead window; dedupeSeed fires it at most once per target (see that handler's
    // own doc comment for the exact scheme).
    key: 'project.target_deadline_approaching',
    module: 'project',
    displayName: 'Target deadline approaching',
    description:
      "A scheduled reminder that an employee target's end date is coming up, with achievement so far.",
    href: '/projects/work-performance',
    requiredVariables: [
      'kpiName',
      'targetValue',
      'achievedValue',
      'unit',
      'endDate',
      'daysRemaining',
      'appName',
    ],
  },

  // ---- Billing -----------------------------------------------------------------
  // Sent to the org's owner and HR admins (the same audience the billing screens themselves are
  // gated to — see roles.ts). `billing.payment.verified`/`.rejected` are triggered from a
  // PlatformAdmin action, not a tenant route — see OfflinePaymentProof's doc comment in
  // schema.prisma for why verification lives outside this org's own workflow entirely.
  {
    key: 'billing.payment.verified',
    module: 'billing',
    displayName: 'Offline payment verified',
    description:
      "A submitted NEFT/RTGS/cash-deposit proof is confirmed and the org's subscription is active.",
    href: '/admin/billing/invoices/{{invoiceId}}',
    requiredVariables: ['reference', 'amount', 'currency', 'planName'],
  },
  {
    key: 'billing.payment.rejected',
    module: 'billing',
    displayName: 'Offline payment rejected',
    description: 'A submitted payment proof could not be matched to a bank credit.',
    href: '/admin/billing/invoices/{{invoiceId}}',
    requiredVariables: ['reference', 'amount', 'currency', 'reason'],
  },
  {
    // Distinct from `.rejected` above (an offline proof that couldn't be matched to a bank
    // credit) — this is the online path: Razorpay's `payment.failed` webhook, a card/UPI/
    // netbanking attempt the bank or issuer itself declined. Different cause, different copy —
    // "resubmit a UTR and deposit slip" would be nonsense here.
    key: 'billing.payment.failed',
    module: 'billing',
    displayName: 'Online payment failed',
    description: 'A card, UPI, or netbanking payment attempt was declined.',
    href: '/admin/billing/invoices/{{invoiceId}}',
    requiredVariables: ['reference', 'amount', 'currency', 'reason'],
  },
  {
    key: 'billing.subscription.overdue',
    module: 'billing',
    displayName: 'Subscription payment overdue',
    description:
      'A subscription has been unpaid past the grace period — the persistent dunning banner and login intercept trigger from this.',
    href: '/admin/billing/invoices',
    requiredVariables: ['invoiceReference', 'amount', 'currency', 'daysOverdue'],
  },
  // ---- Asset management -------------------------------------------------------
  {
    key: 'asset.requested',
    module: 'asset',
    displayName: 'Asset requested',
    description: "An employee's asset request needs the current approver to act on it.",
    href: '/asset-management/requests/{{assetRequestId}}',
    requiredVariables: ['requesterName', 'categoryName', 'requestCode', 'justification', 'appName'],
  },
  {
    key: 'asset.request_approved',
    module: 'asset',
    displayName: 'Asset request approved',
    description: 'An asset request is fully approved and ready for allocation.',
    href: '/asset-management/requests/{{assetRequestId}}',
    requiredVariables: ['approverName', 'categoryName', 'requestCode'],
  },
  {
    key: 'asset.request_rejected',
    module: 'asset',
    displayName: 'Asset request declined',
    description: "An employee's asset request is declined.",
    href: '/asset-management/requests/{{assetRequestId}}',
    requiredVariables: ['approverName', 'categoryName', 'requestCode', 'reason'],
  },
  {
    key: 'asset.allocated',
    module: 'asset',
    displayName: 'Asset allocated to you',
    description: 'An asset is assigned to the employee and needs acknowledgement.',
    href: '/asset-management/assets/{{assetId}}',
    requiredVariables: ['assetName', 'assetCode', 'assignedByName'],
  },
  {
    key: 'asset.transferred',
    module: 'asset',
    displayName: 'Asset transferred',
    description: 'Custody of an asset changed from one person to another.',
    href: '/asset-management/assets/{{assetId}}',
    requiredVariables: ['assetName', 'assetCode', 'fromName', 'toName'],
  },
  {
    key: 'asset.return_requested',
    module: 'asset',
    displayName: 'Asset return requested',
    description: 'An employee flags an asset for return and asset admins should act on it.',
    href: '/asset-management/requests?tab=allocations',
    requiredVariables: ['assetName', 'assetCode', 'requesterName'],
  },
  {
    key: 'asset.return_due',
    module: 'asset',
    displayName: 'Asset return due',
    description: 'A person who is leaving is asked to hand back a company asset they hold.',
    href: '/asset-management/my-assets',
    requiredVariables: ['assetName', 'assetCode'],
  },
  {
    key: 'asset.returned',
    module: 'asset',
    displayName: 'Asset returned',
    description: 'An asset is returned and its condition recorded.',
    href: '/asset-management/assets/{{assetId}}',
    requiredVariables: ['assetName', 'assetCode', 'condition'],
  },
  {
    key: 'asset.maintenance_created',
    module: 'asset',
    displayName: 'Maintenance ticket reported',
    description: 'A hardware issue is reported against an asset and asset admins should triage it.',
    href: '/asset-management/maintenance/{{issueId}}',
    requiredVariables: ['issueCode', 'assetName', 'assetCode', 'title', 'reporterName'],
  },
  {
    key: 'asset.maintenance_assigned',
    module: 'asset',
    displayName: 'Maintenance ticket assigned to you',
    description: 'A technician is assigned to a maintenance ticket.',
    href: '/asset-management/maintenance/{{issueId}}',
    requiredVariables: ['issueCode', 'assetName', 'assetCode', 'title'],
  },
  {
    key: 'asset.maintenance_status_changed',
    module: 'asset',
    displayName: 'Maintenance ticket status changed',
    description: 'The status of a maintenance ticket changes.',
    href: '/asset-management/maintenance/{{issueId}}',
    requiredVariables: ['issueCode', 'assetName', 'assetCode', 'fromStatus', 'toStatus'],
  },
  {
    key: 'asset.maintenance_resolved',
    module: 'asset',
    displayName: 'Maintenance ticket resolved',
    description: 'A maintenance ticket is resolved and closed.',
    href: '/asset-management/maintenance/{{issueId}}',
    requiredVariables: ['issueCode', 'assetName', 'assetCode'],
  },
  {
    key: 'asset.request_cancelled',
    module: 'asset',
    displayName: 'Asset request cancelled',
    description: 'An asset request is cancelled before it is fulfilled.',
    href: '/asset-management/requests/{{assetRequestId}}',
    requiredVariables: ['requestCode', 'categoryName', 'cancelledByName'],
  },
  {
    key: 'asset.acknowledgement_pending',
    module: 'asset',
    displayName: 'Asset acknowledgement pending',
    description: 'An allocated asset has gone unacknowledged for several days.',
    href: '/asset-management/my-assets',
    requiredVariables: ['assetName', 'assetCode', 'daysPending'],
  },
  {
    key: 'asset.warranty_expiring',
    module: 'asset',
    displayName: 'Asset warranty expiring',
    description: 'An asset warranty ends within 30 days.',
    href: '/asset-management/assets?filter=warranty_expiring',
    requiredVariables: ['assetName', 'assetCode', 'expiresOn', 'daysLeft'],
  },
  {
    key: 'asset.contract_expiring',
    module: 'asset',
    displayName: 'Maintenance contract expiring',
    description: 'A vendor maintenance contract ends within 30 days.',
    href: '/asset-management/administration/vendors',
    requiredVariables: ['vendorName', 'expiresOn', 'daysLeft'],
  },
  {
    key: 'asset.return_overdue',
    module: 'asset',
    displayName: 'Asset return overdue',
    description: 'A requested asset return is still outstanding after several days.',
    href: '/asset-management/requests?tab=allocations',
    requiredVariables: ['assetName', 'assetCode', 'holderName', 'daysOverdue'],
  },

  // ---- Company policies (ADR 0026) ---------------------------------------------
  {
    key: 'policy.published',
    module: 'policy',
    displayName: 'Policy published',
    description: 'A company policy that applies to this person is published or updated.',
    href: '/organisation/policies/{{policyId}}',
    requiredVariables: [
      'policyTitle',
      'versionNumber',
      'effectiveFrom',
      'acknowledgementNote',
      'appName',
    ],
  },
  {
    key: 'policy.acknowledgement_reminder',
    module: 'policy',
    displayName: 'Policy acknowledgement reminder',
    description: 'This person has not yet acknowledged a policy that requires it.',
    href: '/organisation/policies/{{policyId}}',
    requiredVariables: ['policyTitle', 'versionNumber', 'dueNote', 'appName'],
  },
  {
    key: 'policy.group_run_finished',
    module: 'policy',
    displayName: 'Group policy run finished',
    description:
      'A group owner published or saved a policy across several companies; sent to them once every company is done.',
    href: '/admin/organisation/policies',
    requiredVariables: ['policyTitle', 'outcome', 'appName'],
  },

  // ---- Training (Training_Module_Plan.md §7) -----------------------------------
  {
    key: 'training.assigned',
    module: 'training',
    displayName: 'Training assigned',
    description: 'HR or a manager assigns a training task to an employee.',
    href: '/learning/{{assignmentId}}',
    // `dueNote` is `trainingAssignedDueNote()`'s ", due <local time>" or " with no due date" — a
    // training assignment may have no due date (lifetime access).
    requiredVariables: [
      'inviteeName',
      'taskTitle',
      'dueNote',
      'allottedHours',
      'assignedByName',
      'courseUrl',
      'courseCardHtml',
    ],
  },
  {
    // apps/worker's training.sweep (Training_Module_Plan.md §7) — dueAt - policy.dueSoonLeadMinutes
    // <= now < dueAt, dueSoonNotifiedAt still null.
    key: 'training.due_soon',
    module: 'training',
    displayName: 'Training due soon',
    description: "A training task's deadline is approaching and it is not yet completed.",
    href: '/learning/{{assignmentId}}',
    requiredVariables: [
      'inviteeName',
      'taskTitle',
      'dueAt',
      'dueTime',
      'minutesLeft',
      'nudgeLine',
      'courseUrl',
      'courseCardHtml',
    ],
  },
  {
    // apps/worker's training.sweep, summary pass: once a day at 6 pm (the recipient's time), only when
    // something happened, to whoever assigned training. Replaces one email per completion.
    key: 'training.summary_hr',
    module: 'training',
    displayName: 'Training daily summary (HR)',
    description:
      'One short email a day at 6 pm to whoever assigns training: how many people completed each course ' +
      'today (and how many late) and how many became overdue. Only sent on days something happened.',
    href: '/admin/training/reports',
    requiredVariables: [
      'inviteeName',
      'summaryDate',
      'headline',
      'summaryHtml',
      'courseActionsHtml',
      'courseUrl',
    ],
  },
  {
    // The same, for a reporting manager's direct reports.
    key: 'training.summary_manager',
    module: 'training',
    displayName: 'Training daily summary (manager)',
    description:
      'One short email a day at 6 pm to a reporting manager: how many of their direct reports completed ' +
      'each course today (and how many late) and how many became overdue. Only sent on days something happened.',
    href: '/learning',
    requiredVariables: [
      'inviteeName',
      'summaryDate',
      'headline',
      'summaryHtml',
      'courseActionsHtml',
      'courseUrl',
    ],
  },
  {
    // apps/worker's training.sweep — the morning of the due date, 10:30-12:30 in the learner's
    // location timezone, due later that day; once per due date (dedupe key).
    key: 'training.due_today',
    module: 'training',
    displayName: 'Training due today',
    description: 'A morning reminder (10:30 am) on the day a course is due and not yet completed.',
    href: '/learning/{{assignmentId}}',
    requiredVariables: [
      'inviteeName',
      'taskTitle',
      'dueAt',
      'dueTime',
      'nudgeLine',
      'courseUrl',
      'courseCardHtml',
    ],
  },
  {
    // apps/worker's training.sweep, pending-digest pass — one email per learner per working day, 30
    // minutes after their shift starts, listing every course still open. Opt-in on every channel: a
    // company turns it on under per-notification settings; the module toggle alone does not.
    key: 'training.pending_start_of_day',
    module: 'training',
    displayName: 'Pending training — start of day',
    description:
      'A start-of-day reminder (30 minutes after the shift starts) listing the courses an employee ' +
      'has not finished yet. Not sent on a weekly off, a holiday or approved leave.',
    href: '/learning',
    requiredVariables: [
      'inviteeName',
      'pendingCount',
      'pendingLabel',
      'pendingListHtml',
      'courseActionsHtml',
      'courseUrl',
    ],
    defaultDisabledChannels: ['email', 'in_app', 'push', 'web_push', 'sms', 'whatsapp', 'slack'],
  },
  {
    // The same digest an hour before the shift ends.
    key: 'training.pending_end_of_day',
    module: 'training',
    displayName: 'Pending training — end of day',
    description:
      'An end-of-day reminder (1 hour before the shift ends) listing the courses an employee has ' +
      'not finished yet. Not sent on a weekly off, a holiday or approved leave.',
    href: '/learning',
    requiredVariables: [
      'inviteeName',
      'pendingCount',
      'pendingLabel',
      'pendingListHtml',
      'courseActionsHtml',
      'courseUrl',
    ],
    defaultDisabledChannels: ['email', 'in_app', 'push', 'web_push', 'sms', 'whatsapp', 'slack'],
  },
  {
    // apps/worker's training.sweep — now >= dueAt, not completed, overdueNotifiedAt still null. Sent
    // to the assignee AND their reporting manager (plan §7's table), also re-sent by the admin
    // monitor's "Nudge" action.
    key: 'training.overdue',
    module: 'training',
    displayName: 'Training overdue',
    description:
      'A training task passed its due time without being completed. Sent to the assignee and ' +
      'their manager.',
    href: '/learning/{{assignmentId}}',
    requiredVariables: [
      'inviteeName',
      'taskTitle',
      'dueAt',
      'nudgeLine',
      'courseUrl',
      'courseCardHtml',
    ],
  },
  {
    key: 'training.completed',
    module: 'training',
    displayName: 'Training completed',
    description:
      'An assignee finishes a training task on time. Sent to whoever assigned it. Email is off by default: the ' +
      'daily training summary replaces one email per person.',
    defaultDisabledChannels: ['email'],
    // Sent to HR / the assigner, never the learner: open the course's Learners tab.
    href: '/admin/training/courses/{{taskId}}?tab=learners',
    requiredVariables: ['personName', 'taskTitle'],
  },
  {
    key: 'training.completed_late',
    module: 'training',
    displayName: 'Training completed late',
    description:
      'An assignee finishes a training task after its due time. Sent to whoever assigned it. Email is off by ' +
      'default: the daily training summary replaces one email per person.',
    defaultDisabledChannels: ['email'],
    // Sent to HR / the assigner, never the learner: open the course's Learners tab.
    href: '/admin/training/courses/{{taskId}}?tab=learners',
    requiredVariables: ['personName', 'taskTitle', 'overrunMinutes'],
  },
  {
    key: 'training.incomplete',
    module: 'training',
    displayName: 'Training marked incomplete',
    description:
      'A training assignment closes as incomplete — the overrun cap, an HR release, employment ending, or HR closing it directly.',
    href: '/learning/{{assignmentId}}',
    requiredVariables: ['personName', 'taskTitle', 'reasonLabel'],
  },
  {
    key: 'training.issue_reported',
    module: 'training',
    displayName: 'Training issue reported',
    description: 'An assignee reports a camera or content problem on a training task.',
    // Sent to HR / the assigner, never the learner: open the course's Learners tab.
    href: '/admin/training/courses/{{taskId}}?tab=learners',
    requiredVariables: ['personName', 'taskTitle', 'categoryLabel'],
  },
  {
    key: 'training.webcam_waived',
    module: 'training',
    displayName: 'Webcam requirement waived',
    description: 'HR waives the webcam recording requirement for one training assignment.',
    href: '/learning/{{assignmentId}}',
    requiredVariables: ['taskTitle'],
  },
  {
    key: 'training.regularisation_approved',
    module: 'training',
    displayName: 'Training overrun regularisation approved',
    description: "An employee's training overrun regularisation request is approved.",
    href: '/attendance/requests',
    requiredVariables: ['taskTitle', 'date', 'overrunMinutes'],
  },
  {
    key: 'training.regularisation_rejected',
    module: 'training',
    displayName: 'Training overrun regularisation declined',
    description: "An employee's training overrun regularisation request is declined.",
    href: '/attendance/requests',
    requiredVariables: ['taskTitle', 'date', 'overrunMinutes'],
  },
  {
    key: 'training.clock_out_released',
    module: 'training',
    displayName: 'Training clock-out block released',
    description:
      "HR uses the emergency release valve to mark an employee's open blocking training tasks " +
      'incomplete for today, so they can clock out (plan §3.5/§8, self-excluded).',
    href: '/learning',
    requiredVariables: ['taskCount'],
  },
  {
    // [K] apps/worker's training.batch.expand (Training_Module_Plan.md §3.7/§7) — an audience
    // assignment over 200 people finishes expanding in the background. Sent to whoever ran "Assign".
    key: 'training.batch_assigned',
    module: 'training',
    displayName: 'Training batch finished assigning',
    description:
      'A large audience assignment (over 200 people) finishes expanding in the background.',
    href: '/admin/training/tasks',
    requiredVariables: ['taskTitle', 'createdCount', 'skippedCount'],
  },
  {
    // Training redesign (B9): HR or a manager presses "Send reminder" on a course's Learners tab.
    // At most one per assignment per TRAINING_REMINDER_MIN_INTERVAL_HOURS (`last_reminded_at`).
    key: 'training.reminder',
    module: 'training',
    displayName: 'Training reminder',
    description: 'HR or a manager reminds an employee to finish a course assigned to them.',
    href: '/learning/{{assignmentId}}',
    requiredVariables: [
      'inviteeName',
      'taskTitle',
      'dueNote',
      'remindedByName',
      'nudgeLine',
      'courseUrl',
      'courseCardHtml',
    ],
  },
  {
    // apps/worker's training.certificates.expiry (training redesign PLAN §13.3) — once per
    // certificate, 30 days before its last valid day (`expiry_notified_at`), to the holder.
    // `expiresOn` is "12 November 2026"; `expiresIn` is "today" / "tomorrow" / "in 12 days".
    key: 'training.certificate_expiring',
    module: 'training',
    displayName: 'Certificate expiring',
    description: 'A course certificate this person earned expires within 30 days.',
    href: '/learning/certificates',
    requiredVariables: ['courseTitle', 'certificateNo', 'expiresOn', 'expiresIn'],
  },
  {
    // Same job, for a certificate from outside the company that HR recorded for this person
    // (first aid, a vendor course). Not sent once HR deletes the record.
    key: 'training.external_certificate_expiring',
    module: 'training',
    displayName: 'External certificate expiring',
    description:
      'A certificate from outside the company, recorded by HR for this person, expires within 30 days.',
    href: '/learning/certificates',
    requiredVariables: ['title', 'issuer', 'expiresOn', 'expiresIn'],
  },

  // ---- Training › Live sessions (training-live.contract.ts, ADR 0031) ------------
  // Emitted by `@hrms/meetings`' notifications.ts, ONE outbox row per invitee: each payload carries
  // that invitee's own personal join link (`/j/:token`), so a forwarded email identifies whose link
  // it was. The bell/push deep link is the signed-in join route instead, which needs no token. No
  // salary or PII beyond names in any payload.
  {
    key: 'training.live_session.invited',
    module: 'training',
    displayName: 'Invited to a live training session',
    description:
      'HR invites this person to a scheduled live training session (meeting or webinar).',
    href: '/training/live/{{sessionId}}/join',
    requiredVariables: [
      'inviteeName',
      'sessionTitle',
      'startsAtLocal',
      'timezone',
      'hostName',
      'durationLabel',
      'joinUrl',
      'joinActionsHtml',
    ],
  },
  {
    key: 'training.live_session.updated',
    module: 'training',
    displayName: 'Live training session changed',
    description:
      'The time, link or details of a live training session this person is invited to change.',
    href: '/training/live/{{sessionId}}/join',
    requiredVariables: [
      'inviteeName',
      'sessionTitle',
      'startsAtLocal',
      'timezone',
      'hostName',
      'durationLabel',
      'joinUrl',
      'joinActionsHtml',
    ],
  },
  {
    key: 'training.live_session.cancelled',
    module: 'training',
    displayName: 'Live training session cancelled',
    description: 'A live training session this person was invited to is cancelled.',
    href: '/learning',
    requiredVariables: ['inviteeName', 'sessionTitle', 'startsAtLocal', 'timezone', 'cancelReason'],
  },
  {
    // apps/worker's training.live_reminders sweep (every 5 minutes) — once per configured offset
    // (default 24 h and 15 min before the start), recorded in `remindersSentMinutes`.
    key: 'training.live_session.reminder',
    module: 'training',
    displayName: 'Live training session starting soon',
    description: 'A reminder before a live training session this person is invited to starts.',
    href: '/training/live/{{sessionId}}/join',
    requiredVariables: [
      'inviteeName',
      'sessionTitle',
      'startsAtLocal',
      'timezone',
      'hostName',
      'durationLabel',
      'startsInLabel',
      'joinUrl',
      'joinActionsHtml',
    ],
  },
  {
    // apps/worker's training.live_attendance_sync — a person newly crosses TRAINING_LIVE_ABSENTEE_RULE
    // (missed >= 3 of their last 5 countable sessions). Sent to their reporting manager and the host.
    key: 'training.live_session.absentee_alert',
    module: 'training',
    displayName: 'Regular absentee from live training',
    description:
      'Someone has missed most of their recent live training sessions. Sent to their reporting ' +
      'manager and the session host.',
    href: '/admin/training/live/reports?personId={{personId}}',
    requiredVariables: [
      'personName',
      'missedCount',
      'windowCount',
      'sessionTitle',
      'historyUrl',
      'historyActionsHtml',
    ],
  },
  // ---- Announcements (ADR 0029) --------------------------------------------------
  {
    key: 'announcement.published',
    module: 'announcement',
    displayName: 'Announcement published',
    description: 'An announcement that applies to this person has gone live.',
    href: '/engage/announcements/{{announcementId}}',
    requiredVariables: ['announcementTitle', 'excerpt', 'acknowledgementNote', 'appName'],
  },
  {
    // An announcement people already saw was edited: wording changed, so their acknowledgement was
    // cleared and they confirm the new text.
    key: 'announcement.updated',
    module: 'announcement',
    displayName: 'Announcement updated',
    description: 'An announcement this person has already seen has had its wording changed.',
    href: '/engage/announcements/{{announcementId}}',
    requiredVariables: ['announcementTitle', 'appName'],
  },
  {
    // Sent once, roughly 24 hours before a mandatory announcement's acknowledgement deadline, to
    // whoever has not yet acknowledged it (apps/worker's announcement-ack-reminder.ts).
    key: 'announcement.ack_reminder_due',
    module: 'announcement',
    displayName: 'Acknowledgement deadline approaching',
    description:
      'A mandatory announcement this person has not acknowledged is nearing its deadline.',
    href: '/engage/announcements/{{announcementId}}',
    requiredVariables: ['announcementTitle', 'deadline', 'appName'],
  },
  // ---- Engage › Celebrations ------------------------------------------------------
  // Fired once a day by apps/worker's celebrations.ts, only on a day something is actually
  // happening — most days this sends nothing at all.
  {
    key: 'engagement.birthday_today',
    module: 'engagement',
    displayName: "It's your birthday",
    description: 'Sent to the person themselves on their birthday.',
    href: '/engage/celebrations',
    requiredVariables: ['firstName', 'appName', 'celebrationsUrl', 'birthdayCardHtml'],
  },
  {
    key: 'engagement.work_anniversary_today',
    module: 'engagement',
    displayName: 'Your work anniversary',
    description: 'Sent to the person themselves on their work anniversary.',
    href: '/engage/celebrations',
    requiredVariables: [
      'firstName',
      'yearsOfService',
      'yearsLabel',
      'appName',
      'celebrationsUrl',
      'anniversaryCardHtml',
    ],
  },
  {
    // Org-wide digest to everyone except today's celebrants (who get their own notification above).
    key: 'engagement.celebrations_today',
    module: 'engagement',
    displayName: "Today's celebrations",
    description: 'A summary of whose birthday or work anniversary is today, org-wide.',
    href: '/engage/celebrations',
    requiredVariables: [
      'headline',
      'summary',
      'appName',
      'celebrationsUrl',
      'celebrationsCardHtml',
    ],
  },
  {
    key: 'engagement.new_joiner_welcome',
    module: 'engagement',
    displayName: 'Welcome to the company',
    description: 'Sent to a new joiner themselves on their first day.',
    href: '/engage/celebrations',
    requiredVariables: ['firstName', 'appName'],
  },
  {
    key: 'engagement.new_joiner_announced',
    module: 'engagement',
    displayName: 'A new colleague joined your department',
    description: "Sent to a new joiner's department colleagues on their first day.",
    href: '/engage/celebrations',
    requiredVariables: ['newJoinerName', 'departmentName', 'appName'],
  },
  // ---- Engage › Social wall --------------------------------------------------------
  {
    key: 'social.mentioned',
    module: 'social',
    displayName: 'Mentioned on the Social wall',
    description: 'Someone @mentions this person in a Social wall post or comment.',
    href: '/engage/social',
    requiredVariables: ['authorName', 'postExcerpt', 'appName'],
  },
  // ---- Helpdesk (ADR 0028) -------------------------------------------------------
  // Payloads carry the ticket number, subject and category — never the description or a comment
  // body, which may be confidential and would otherwise sit in an email inbox or a push preview.
  {
    key: 'helpdesk.ticket_raised',
    module: 'helpdesk',
    displayName: 'Ticket raised',
    description: "A ticket is raised in a category this person's helpdesk team handles.",
    href: '/helpdesk/tickets/{{ticketId}}',
    requiredVariables: ['ticketNo', 'subject', 'categoryName', 'priority', 'raiserName', 'appName'],
  },
  {
    key: 'helpdesk.ticket_assigned',
    module: 'helpdesk',
    displayName: 'Ticket assigned',
    description: 'A helpdesk ticket is assigned to this person.',
    href: '/helpdesk/tickets/{{ticketId}}',
    requiredVariables: ['ticketNo', 'subject', 'categoryName', 'priority', 'appName'],
  },
  {
    key: 'helpdesk.ticket_commented',
    module: 'helpdesk',
    displayName: 'Ticket reply',
    description:
      'Someone replied on a ticket this person raised or is assigned. Internal notes never reach the raiser.',
    href: '/helpdesk/tickets/{{ticketId}}',
    requiredVariables: ['ticketNo', 'subject', 'authorName', 'appName'],
  },
  {
    key: 'helpdesk.ticket_status_changed',
    module: 'helpdesk',
    displayName: 'Ticket status changed',
    description: 'A ticket this person raised moved to a new status.',
    href: '/helpdesk/tickets/{{ticketId}}',
    requiredVariables: ['ticketNo', 'subject', 'statusLabel', 'appName'],
  },
  {
    key: 'helpdesk.sla_breached',
    module: 'helpdesk',
    displayName: 'Ticket SLA breached',
    description:
      'A ticket missed its first-response or resolution target. Sent to the team lead, the assignee and the Helpdesk Managers.',
    href: '/helpdesk/tickets/{{ticketId}}',
    requiredVariables: ['ticketNo', 'subject', 'categoryName', 'priority', 'slaLabel', 'appName'],
  },
  // ---- Surveys and polls (ADR 0027) --------------------------------------------
  {
    key: 'survey.published',
    module: 'survey',
    displayName: 'Survey published',
    description: 'A survey or poll sent to this person has opened.',
    href: '/engage/surveys/{{surveyId}}',
    requiredVariables: ['surveyTitle', 'closesNote', 'anonymityNote', 'appName'],
  },
  {
    key: 'survey.reminder',
    module: 'survey',
    displayName: 'Survey reminder',
    description: 'This person has not yet answered an open survey sent to them.',
    href: '/engage/surveys/{{surveyId}}',
    requiredVariables: ['surveyTitle', 'closesNote', 'appName'],
  },
  {
    key: 'survey.closed',
    module: 'survey',
    displayName: 'Survey closed',
    description: 'A survey or poll this person owns has closed; its results are ready.',
    href: '/admin/engage/surveys/{{surveyId}}',
    requiredVariables: ['surveyTitle', 'appName'],
  },
  // ---- Agreement ----------------------------------------------------------------
  {
    key: 'agreement.sent',
    module: 'agreement',
    displayName: 'Agreement sent for signature',
    description: 'An agreement is sent to the authorised signatory for e-signature.',
    href: '/admin/agreements',
    requiredVariables: ['companyLegalName'],
  },
  {
    key: 'agreement.signed',
    module: 'agreement',
    displayName: 'Agreement signed',
    description: 'The signatory completes signing the agreement.',
    href: '/admin/agreements',
    requiredVariables: ['companyLegalName'],
  },
  {
    key: 'agreement.rejected',
    module: 'agreement',
    displayName: 'Agreement declined',
    description: 'The signatory declines to sign, or the agreement is voided.',
    href: '/admin/agreements',
    requiredVariables: ['companyLegalName', 'reason'],
  },

  // ---- Travel desk (ADR 0030, plan §5.9) ------------------------------------------
  // Payloads carry the trip reference, destination, dates and people's names — never fares, per
  // diem or passport details. Employee-facing links open the trip; desk-facing ones open the desk
  // queue on that trip. `approval.question` / `approval.answered` land with WF-1, not here.
  {
    key: 'travel.invite.received',
    module: 'travel',
    displayName: 'Invited on a trip',
    description: 'A colleague adds this person as a traveller on a trip they are organising.',
    href: '/travel/trips/{{travelRequestId}}',
    requiredVariables: [
      'inviterName',
      'reference',
      'destination',
      'startDate',
      'endDate',
      'appName',
    ],
  },
  {
    key: 'travel.invite.declined',
    module: 'travel',
    displayName: 'Trip invitation declined',
    description: 'A colleague this person invited on a trip declines the invitation.',
    href: '/travel/trips/{{travelRequestId}}',
    requiredVariables: ['inviteeName', 'reference', 'destination', 'startDate', 'endDate'],
  },
  {
    key: 'travel.request.approved',
    module: 'travel',
    displayName: 'Trip approved',
    description: "A traveller's trip request is approved and passes to the travel desk.",
    href: '/travel/trips/{{travelRequestId}}',
    requiredVariables: [
      'approverName',
      'reference',
      'destination',
      'startDate',
      'endDate',
      'comment',
    ],
  },
  {
    key: 'travel.request.rejected',
    module: 'travel',
    displayName: 'Trip declined',
    description: "A traveller's trip request is declined.",
    href: '/travel/trips/{{travelRequestId}}',
    requiredVariables: [
      'approverName',
      'reference',
      'destination',
      'startDate',
      'endDate',
      'comment',
    ],
  },
  {
    key: 'travel.desk.new_request',
    module: 'travel',
    displayName: 'New trip for the desk',
    description: 'An approved trip arrives in the queue of a travel desk team this person is on.',
    href: '/admin/travel/desk?trip={{travelRequestId}}',
    requiredVariables: [
      'travellerName',
      'reference',
      'destination',
      'startDate',
      'endDate',
      'dueAt',
      'appName',
    ],
  },
  {
    key: 'travel.desk.unassigned',
    module: 'travel',
    displayName: 'Trip with no desk team',
    description:
      'An approved trip matches no travel desk team and no fallback team, so nobody will book it. Sent once per trip to the recipients chosen for this notification.',
    href: '/admin/travel/desk?trip={{travelRequestId}}',
    requiredVariables: ['travellerName', 'reference', 'destination', 'startDate', 'appName'],
  },
  {
    key: 'travel.desk.breached',
    module: 'travel',
    displayName: 'Desk booking target missed',
    description:
      'A trip passes its desk booking target without being booked. Sent once to the team lead and the recipients chosen for this notification.',
    href: '/admin/travel/desk?trip={{travelRequestId}}',
    requiredVariables: [
      'reference',
      'travellerName',
      'teamName',
      'assigneeName',
      'dueAt',
      'appName',
    ],
  },
  {
    key: 'travel.booking.options_shared',
    module: 'travel',
    displayName: 'Booking options to choose from',
    description: 'The travel desk shares booking options for the traveller to pick one.',
    href: '/travel/trips/{{travelRequestId}}',
    requiredVariables: ['agentName', 'reference', 'optionCount', 'respondBy', 'appName'],
  },
  {
    key: 'travel.booking.option_expiring',
    module: 'travel',
    displayName: 'Booking options expiring',
    description:
      'Booking options shared with the traveller expire within a day and none has been picked.',
    href: '/travel/trips/{{travelRequestId}}',
    requiredVariables: ['reference', 'respondBy', 'appName'],
  },
  {
    key: 'travel.booking.confirmed',
    module: 'travel',
    displayName: 'Booking confirmed',
    description:
      'The travel desk confirms a booking on the trip; the ticket is attached to the trip.',
    href: '/travel/trips/{{travelRequestId}}',
    requiredVariables: [
      'reference',
      'modeLabel',
      'bookingReference',
      'destination',
      'startDate',
      'appName',
    ],
  },
  {
    key: 'travel.booking.declined',
    module: 'travel',
    displayName: 'Booking not possible',
    description:
      'The travel desk cannot make a booking as requested and sends the trip back to the traveller.',
    href: '/travel/trips/{{travelRequestId}}',
    requiredVariables: ['agentName', 'reference', 'reason', 'appName'],
  },
  {
    key: 'travel.booking.cancelled',
    module: 'travel',
    displayName: 'Booking cancelled',
    description: 'A confirmed booking on the trip is cancelled.',
    href: '/travel/trips/{{travelRequestId}}',
    requiredVariables: ['reference', 'modeLabel', 'bookingReference', 'reason', 'appName'],
  },
  {
    key: 'travel.booking.refund_received',
    module: 'travel',
    displayName: 'Booking refund received',
    description: 'The travel desk records the refund for a cancelled booking as received.',
    href: '/travel/trips/{{travelRequestId}}',
    requiredVariables: ['reference', 'modeLabel', 'bookingReference', 'appName'],
  },
  {
    key: 'travel.amendment.approved',
    module: 'travel',
    displayName: 'Trip change approved',
    description: 'A requested change to an approved trip is approved.',
    href: '/travel/trips/{{travelRequestId}}',
    requiredVariables: ['approverName', 'reference', 'comment'],
  },
  {
    key: 'travel.amendment.rejected',
    module: 'travel',
    displayName: 'Trip change declined',
    description: 'A requested change to an approved trip is declined; the trip stays as it was.',
    href: '/travel/trips/{{travelRequestId}}',
    requiredVariables: ['approverName', 'reference', 'comment'],
  },
  {
    key: 'travel.trip.upcoming',
    module: 'travel',
    displayName: 'Trip tomorrow',
    description:
      "The evening before a booked trip starts, a reminder of the traveller's itinerary.",
    href: '/travel/trips/{{travelRequestId}}',
    requiredVariables: ['reference', 'destination', 'startDate', 'appName'],
    // WhatsApp is opt-in: useful there, but only for a company that has switched it on.
    defaultDisabledChannels: ['whatsapp'],
  },
  {
    key: 'travel.trip.expired',
    module: 'travel',
    displayName: 'Trip expired unbooked',
    description: 'A trip passes its end date without ever being booked and is closed as expired.',
    href: '/travel/trips/{{travelRequestId}}',
    requiredVariables: ['reference', 'destination', 'endDate', 'appName'],
  },
  {
    key: 'travel.trip.cancelled_exit',
    module: 'travel',
    displayName: 'Trip cancelled for an exit',
    description:
      "A trip falling after a departing employee's last working day is cancelled automatically.",
    href: '/travel/trips/{{travelRequestId}}',
    requiredVariables: [
      'travellerName',
      'reference',
      'destination',
      'startDate',
      'lastWorkingDay',
      'appName',
    ],
  },
  {
    key: 'travel.trip.settle_expenses',
    module: 'travel',
    displayName: 'Settle trip expenses',
    description: 'A trip has ended and the traveller has not yet submitted its expense settlement.',
    href: '/travel/trips/{{travelRequestId}}',
    requiredVariables: ['reference', 'endDate', 'settleBy', 'appName'],
  },
  {
    key: 'travel.settlement.decided',
    module: 'travel',
    displayName: 'Trip settlement decided',
    description: "A traveller's expense settlement for a trip is approved or sent back.",
    href: '/travel/trips/{{travelRequestId}}',
    requiredVariables: ['approverName', 'reference', 'decisionLabel', 'comment', 'appName'],
  },
  {
    key: 'travel.settlement.settled',
    module: 'travel',
    displayName: 'Trip settlement settled',
    description:
      "Finance nets a trip's approved expenses off against its advance; the traveller learns what is paid to them or still owed.",
    href: '/travel/trips/{{travelRequestId}}',
    requiredVariables: ['reference', 'outcomeLabel', 'appName'],
  },
  {
    key: 'travel.trip.closing_soon',
    module: 'travel',
    displayName: 'Trip closing soon',
    description:
      'The window for adding expenses to a finished trip closes soon; after that the trip is closed.',
    href: '/travel/trips/{{travelRequestId}}',
    requiredVariables: ['reference', 'closesOn', 'appName'],
    // Same WhatsApp opt-in as travel.trip.upcoming.
    defaultDisabledChannels: ['whatsapp'],
  },
  {
    key: 'travel.onduty_locked',
    module: 'travel',
    displayName: 'Travel days in a locked attendance period',
    description:
      "A trip's on-duty days fall in an attendance period that is already locked, so they were left unchanged. Sent to HR to correct by hand.",
    href: '/attendance',
    requiredVariables: ['travellerName', 'reference', 'startDate', 'endDate', 'appName'],
  },

  // ---- Travel desk, run 2 additions (ADR 0030) -------------------------------------
  // Still no fare, per diem or passport detail in any payload.
  {
    key: 'travel.invite.accepted',
    module: 'travel',
    displayName: 'Trip invitation accepted',
    description: 'A colleague this person invited on a trip accepts the invitation.',
    href: '/travel/trips/{{travelRequestId}}',
    requiredVariables: ['inviteeName', 'reference', 'destination', 'startDate', 'endDate'],
  },
  {
    key: 'travel.invite.expired',
    module: 'travel',
    displayName: 'Trip invitation lapsed',
    description:
      'An invitation to join a trip was never answered and was removed by the weekly clean-up.',
    requiredVariables: ['inviterName', 'reference', 'destination', 'appName'],
  },
  {
    key: 'travel.request.skip_level_pending',
    module: 'travel',
    displayName: 'Trip needs your second approval',
    description:
      "A trip that was filed after the fact or breaks policy passed the manager's approval and now waits on this approver.",
    href: '/approvals?tab=travel',
    requiredVariables: [
      'requesterName',
      'reference',
      'destination',
      'startDate',
      'endDate',
      'reasonLabel',
      'appName',
    ],
  },
  {
    key: 'travel.amendment.pending',
    module: 'travel',
    displayName: 'Trip change waiting on you',
    description: 'A traveller asks to change an approved trip and this person must decide.',
    href: '/approvals?tab=travel',
    requiredVariables: ['requesterName', 'reference', 'changeSummary', 'appName'],
  },
  {
    key: 'travel.desk.assigned',
    module: 'travel',
    displayName: 'Trip assigned to you',
    description: 'A trip in a desk team queue is assigned or reassigned to this person.',
    href: '/admin/travel/desk?trip={{travelRequestId}}',
    requiredVariables: [
      'assignerName',
      'travellerName',
      'reference',
      'destination',
      'startDate',
      'dueAt',
      'appName',
    ],
  },
  {
    key: 'travel.desk.amendment_received',
    module: 'travel',
    displayName: 'Approved trip change for the desk',
    description:
      'A change to a trip already in the desk queue was approved; the bookings need updating.',
    href: '/admin/travel/desk?trip={{travelRequestId}}',
    requiredVariables: ['travellerName', 'reference', 'destination', 'changeSummary', 'appName'],
  },
  {
    key: 'travel.booking.needs_action',
    module: 'travel',
    displayName: 'Trip needs your action',
    description:
      'The desk could not complete a booking, or the shared options lapsed, and the traveller must amend, self-book or drop the trip.',
    href: '/travel/trips/{{travelRequestId}}',
    requiredVariables: ['reference', 'reasonLabel', 'appName'],
  },
  {
    key: 'travel.booking.refund_overdue',
    module: 'travel',
    displayName: 'Booking refund overdue',
    description:
      'A cancelled booking has waited more than 30 days for its refund. Sent weekly to the team lead until it is recorded.',
    href: '/admin/travel/desk?trip={{travelRequestId}}',
    requiredVariables: [
      'travellerName',
      'reference',
      'modeLabel',
      'bookingReference',
      'daysPending',
      'appName',
    ],
  },
  {
    key: 'travel.trip.settle_escalation',
    module: 'travel',
    displayName: 'Trip settlement overdue',
    description:
      "A team member's trip ended and its expenses are still unsettled after the reminder period; sent to their reporting manager.",
    href: '/travel/trips/{{travelRequestId}}',
    requiredVariables: ['travellerName', 'reference', 'endDate', 'daysOverdue', 'appName'],
  },

  // ---- Performance remediation T2.9 / T2.10: calibration countersign and results release ----
  {
    key: 'review.results_released',
    module: 'review',
    displayName: 'Review results released',
    description:
      "HR released a review cycle's results; sent to each reviewee. Carries no score — the result is read in the app.",
    href: '/performance/reviews/{{reviewId}}',
    requiredVariables: ['cycleLabel', 'appName'],
  },
  {
    key: 'review.cancelled',
    module: 'review',
    displayName: 'Performance review withdrawn',
    description: "HR cancelled this person's review before results were released.",
    href: '/performance/reviews/{{reviewId}}',
    requiredVariables: ['cycleLabel', 'appName'],
  },
  {
    key: 'calibration.rejected',
    module: 'calibration',
    displayName: 'Calibration sent back',
    description:
      "A calibration session's HR countersign was rejected (or could not apply); sent to the facilitator. Nothing was applied.",
    href: '/admin/performance/calibration',
    requiredVariables: ['appName'],
  },

  // ---- Performance remediation T2.11: returned / sent back / appeal reopened ----
  {
    key: 'review.returned_to_employee',
    module: 'review',
    displayName: 'Review returned for changes',
    description:
      "The manager (or HR) returned this person's review to their self-assessment, with a comment on what to revise.",
    href: '/performance/reviews/{{reviewId}}',
    requiredVariables: ['cycleLabel', 'commentExcerpt', 'returnComment', 'dueDate', 'appName'],
  },
  {
    key: 'review.sent_back_to_manager',
    module: 'review',
    displayName: 'Assessment sent back by HR',
    description:
      "HR's outlier review rejected the manager's assessment; sent to the manager of record to revise and resubmit.",
    href: '/performance/reviews/{{reviewId}}',
    requiredVariables: ['subjectName', 'cycleLabel', 'hrComment', 'dueDate', 'appName'],
  },
  {
    key: 'review.appeal.reopened',
    module: 'review',
    displayName: 'Appeal upheld — review reopened',
    description:
      "HR upheld an employee's appeal and reopened the review; sent to the manager of record to re-assess. Carries no score.",
    href: '/performance/reviews/{{reviewId}}',
    requiredVariables: ['subjectName', 'cycleLabel', 'resolutionComment', 'appName'],
  },
  // Performance remediation Phase 3 — merit. No amounts in either payload.
  {
    key: 'merit.cycle_decided',
    module: 'performance',
    displayName: 'Merit round decided',
    description:
      'A merit round was finally approved or rejected; sent to the HR member who submitted it and the managers whose recommendations it covered.',
    href: '/performance/reviews/merit/{{cycleId}}',
    requiredVariables: ['cycleLabel', 'decision', 'cycleId', 'appName'],
  },
  {
    key: 'merit.revision_applied',
    module: 'performance',
    displayName: 'Merit increase applied by Payroll',
    description:
      "Payroll applied an approved merit increase to an employee's pay; sent to the HR member who submitted the round. Never to the employee, and no amounts.",
    href: '/admin/performance/review-cycles/{{cycleId}}/roster?tab=merit',
    requiredVariables: ['personName', 'cycleLabel', 'effectiveDate', 'cycleId', 'appName'],
  },
  // Performance remediation N18 — the export worker could not produce a requested file (storage
  // not configured, or the job's final attempt failed). Generic: no error text, names or scores.
  {
    key: 'performance.insights.export_failed',
    module: 'performance',
    displayName: 'Performance Insights export failed',
    description:
      'A requested Performance Insights export could not be produced; sent to the person who requested it, so they can request it again.',
    href: '/performance/insights',
    requiredVariables: ['scopeLabel', 'jobId', 'appName'],
  },
  // ---- Connected applications (ADR 0033) ---------------------------------------
  // Sent to whoever holds app_integration:manage at organisation scope (each company decides who).
  // Never one per employee: a failure summary is at most hourly per application.
  {
    key: 'app_integration.breaker_opened',
    module: 'app_integration',
    displayName: 'Application paused after repeated failures',
    description:
      'A connected application kept failing, so PeopleOrbit stopped sending to it. Sends wait until someone resumes it.',
    href: '/admin/settings/integrations/applications/{{applicationId}}',
    requiredVariables: ['applicationName', 'applicationId', 'appName'],
  },
  {
    key: 'app_integration.failures_digest',
    module: 'app_integration',
    displayName: 'Sends to an application failed',
    description:
      'Some employees could not be sent to a connected application in the last hour. At most one per application per hour.',
    href: '/admin/settings/integrations/applications/{{applicationId}}',
    requiredVariables: ['applicationName', 'applicationId', 'failedCount', 'appName'],
  },
  {
    key: 'app_integration.batch_finished',
    module: 'app_integration',
    displayName: 'Bulk send finished',
    description:
      'A sync of existing employees, a resync, or a send of waiting employees has finished; sent to whoever started it.',
    href: '/admin/settings/integrations/applications/{{applicationId}}',
    requiredVariables: [
      'applicationName',
      'applicationId',
      'batchLabel',
      'syncedCount',
      'failedCount',
      'appName',
    ],
  },
  {
    key: 'app_integration.dry_run_reminder',
    module: 'app_integration',
    displayName: 'Application still in dry run',
    description:
      'A connected application has been in dry run for three days with employees waiting to be sent. Nothing has been sent yet.',
    href: '/admin/settings/integrations/applications/{{applicationId}}',
    requiredVariables: ['applicationName', 'applicationId', 'waitingCount', 'appName'],
  },
];

const CATALOG_BY_KEY = new Map<string, NotificationEvent>(
  NOTIFICATION_EVENT_CATALOG.map((event) => [event.key, event]),
);

// ---------------------------------------------------------------------------
// Lookups
// ---------------------------------------------------------------------------

/**
 * The catalogue entry for `key`, or throws.
 *
 * THROWING RATHER THAN RETURNING NULL IS THE POINT: this is the function a future
 * `emitNotification()` calls before writing the outbox row, and its whole job is to reject a module
 * that tries to invent an event key that was never declared here. A null return would let a caller
 * treat "unknown key" as just another falsy case to shrug past; a thrown error surfaces it as the
 * contract violation it is, in the caller's own transaction, before anything is written.
 */
export function notificationEvent(key: string): NotificationEvent {
  const event = CATALOG_BY_KEY.get(key);
  if (!event) {
    throw new Error(
      `Unknown notification event key ${JSON.stringify(key)} — it is not declared in ` +
        'NOTIFICATION_EVENT_CATALOG. Add it there first; modules cannot invent event keys.',
    );
  }
  return event;
}

const HREF_PLACEHOLDER = /\{\{\s*([A-Za-z][A-Za-z0-9_]*)\s*\}\}/g;

/**
 * Render `event.href` against a payload, or `null` if it cannot be rendered safely.
 *
 * DELIBERATELY NOT `packages/notify`'s `interpolate()`, for three independent reasons, each one
 * enough on its own:
 *
 *   1. A missing variable there renders as `''`, turning `/leave/requests/{{id}}` into
 *      `/leave/requests/` — a plausible-looking link to the WRONG screen. A link a person taps in
 *      good faith should never silently point at the wrong record; returning `null` here instead
 *      means "no link", which a bell UI can render as a plain, unclickable notification.
 *   2. Its escapers are HTML/text escapers for message bodies, not URL-safe encoding for a path
 *      segment — a value containing `&`, `?`, or `#` would corrupt the route rather than merely
 *      display oddly.
 *   3. The result is handed to a client-side router. An event payload value is data a module wrote,
 *      not something this function can trust to already be a safe internal path — so an
 *      unvalidated value reaching this template would make `resolveHref` an open-redirect primitive
 *      carried by our own notifications. Hence: `encodeURIComponent` on every substitution, `null`
 *      on any missing/null/undefined variable, and a hard rule that the result names an internal
 *      path (exactly one leading slash) and never a protocol-relative one (`//host/...`, which
 *      leaves our origin entirely).
 */
export function resolveHref(
  event: NotificationEvent,
  payload: Readonly<Record<string, unknown>>,
): string | null {
  if (!event.href) return null;

  let missing = false;
  const resolved = event.href.replace(HREF_PLACEHOLDER, (_match, name: string) => {
    const value = payload[name];
    if (value === null || value === undefined) {
      missing = true;
      return '';
    }
    return encodeURIComponent(String(value));
  });

  if (missing) return null;
  // Exactly one leading slash: `//host/path` is protocol-relative and would send the browser to a
  // different origin entirely, not a route within this app.
  if (!/^\/(?!\/)/.test(resolved)) return null;

  return resolved;
}
